// Sales i18n namespace (Module 09 §4, slice P3-A part 2). Wired into apps/web's
// aggregated `dictionaries` (src/i18n-dictionaries.ts) so the CI key-parity gate
// (`pnpm run i18n:parity`, TESTING §12.1 / D-014) covers it automatically, and
// declared in src/i18n-completeness.ts so the VALUE gate knows which locales are
// real.
//
// `salesEn` is authoritative; `SalesDict = Translated<typeof salesEn>` makes
// ru/zh/fr parity-checked at COMPILE time (a missing or extra key fails tsc).
// `ru` is a real translation (sales.ru.ts) — the design partner's sales
// managers price cable in Russian and talk to their customers in Russian; zh/fr
// are English placeholders, stated in i18n-completeness.ts rather than implied
// (D-122).
//
// THREE WORDS THIS MODULE HAD TO SETTLE IN BOTH LANGUAGES AT ONCE, because a
// term settled in English and left to a later translator is not settled:
//
//   `tier`          → «ценовая категория» — NOT «уровень» (which reads as a
//                     rank of the customer) and not «класс» (module 05 already
//                     owns that word for remnant price classes).
//   `credit hold`   → «кредитный стоп» — the phrase a Kazakh finance lead
//                     already says; «блокировка» is module 04's operation flag
//                     and module 07's quarantine, and a fourth thing wearing it
//                     would make all four unreadable.
//   `margin guard`  → «порог маржи» — a THRESHOLD, not a «защита»: what it does
//                     is refuse a price below a number, and the Russian has to
//                     say the number.
//
// Two more, for the same reason: `off-cut` → «обрезок» (the piece; module 05's
// remnant registry says the same word for the same physical thing), and `price
// list` → «прайс-лист», which is what the документооборот actually calls it.
//
// SERVER PROBLEM TITLES ARE NOT TRANSLATED (D-022): a refusal renders the
// server's own sentence, labelled by `common.serverSaid`, so the reader can see
// where the words came from. The machinery for translatable field errors is
// `errors[].message_key`, not this file.
//
// ENTITY NOUNS ARE NOT IN HERE either. Customer names, price-list codes,
// product SKUs and off-cut class names are TENANT DATA (`name_i18n` on the row)
// — which is exactly why the off-cut class editor shows one name field per
// locale instead of one field.

import type { Translated } from "../../../i18n-shape.js";
import { salesRuDict } from "./sales.ru.js";

export const salesEn = {
  nav: {
    aria: "Sales sections",
    customers: "Customers",
    quotes: "Quotes",
    orders: "Orders",
    reviews: "Reviews",
    priceLists: "Price lists",
    offcutClasses: "Off-cut discounts",
    marginGuards: "Margin guards",
    // P3-E PR-E2 (D-292 rulings 1, 11): the manager's task list and the
    // screen that holds the reminder and automatic-step settings.
    tasks: "Tasks",
    settings: "Settings",
    // P3-E PR-E3 (D-293): §4.9's numbers.
    analytics: "Analytics",
    // P3-E2 PR E2-1 (spec 09 §4.14): the board, the deal list, the deal page
    // and the forecast share this one entry, after Customers.
    pipeline: "Pipeline",
    // P3-UX (D-445): the general settings screen inside the Settings tab, and
    // the names of the second strips under a grouped tab.
    general: "General",
    groupAria: {
      tasks: "Tasks and reviews",
      settings: "Sales settings",
    },
  },

  common: {
    loading: "Loading…",
    // P3-UX (D-445): the words of every sales FilterBar.
    filters: "Filters",
    filtersCount: "Filters ({{count}})",
    filtersTitle: "Filters",
    filtersDone: "Done",
    filterRemove: "Remove {{filter}}",
    filtersClear: "Clear all",
    filterChip: "{{filter}}: {{value}}",
    otherLanguages: "Other languages",
    // Shared by §4.5's and §4.7's customer chip — one label, because the two
    // screens clear the same filter and a reader who learns it once should not
    // meet a second wording on the next tab.
    filterCustomerClear: "Show every customer",
    retry: "Try again",
    requestId: "Request ID",
    // The label in front of the server's own words — never a paraphrase of
    // them (D-022).
    serverSaid: "Refused: {{title}}",
    // 503 RESOURCE_BUSY (D-318): the customer was edited while each of three
    // pricing passes ran, and nothing was written, so the same press can be
    // made again. Said in place of the server's English title, in the words
    // `inventory.errors.RESOURCE_BUSY` uses for the same code.
    busyRetry: "Someone else is changing this right now. Try again in a moment.",
    loadMore: "Show more",
    pageRange: "Showing {{from}}–{{to}}",
    pageRangeOf: "Showing {{from}}–{{to}} of {{total}}",
    pagePrev: "Previous page",
    pageNext: "Next page",
    close: "Close",
    cancel: "Cancel",
    save: "Save",
    saving: "Saving…",
    edit: "Edit",
    // A filter the address holds whose value the control's own list cannot
    // name (a view an admin shared, read by someone whose owner list is
    // empty). The option keeps the filter visible, so it can be cleared.
    notListed: "Not in this list",
  },

  access: {
    title: "Not available to your role",
    area: "Sales holds nothing for your role. Ask a group admin if you need access.",
    customers: "The customer book is for sales, finance, planning and the admins.",
    // §4.18 (E2-2c): the page's gate is the import lane's own code
    // (`onboarding.import.customers`, spec 02 §7), not the book's.
    importCustomers: "Importing customers is for sales and the admins.",
    importOrders: "Importing open orders is for sales and the admins.",
    pricing: "Price lists and discounts are for sales and the admins.",
    // D-301: the server refused a price list's items to a reader without
    // `sales.read_value`. Said as a fact about the role, never as a failure.
    pricingValue: "The prices on this price list are not shown to your role.",
    guards: "Margin guards are part of pricing governance — group admins configure them.",
    quotes: "The quote book is for the people who write quotes — sales and the admins.",
    reviews: "The review inbox is for the people who decide breaches and credit holds.",
    orders: "Orders are for sales, planning, finance, quality and the admins.",
    tasks: "Tasks are for sales managers and the admins.",
    settings: "The sales settings are open to everyone who reads orders.",
    analytics: "Sales analytics are for the desks that own a document — sales and the admins.",
    // §4.14's four pages. Their audience is the customer book's (§7
    // narrowing 15), so qc and the storekeeper read this sentence.
    pipeline: "The pipeline is for sales, finance, planning and the admins.",
  },

  tier: {
    market: "Market",
    b2b: "B2B",
    distributor: "Distributor",
  },

  channel: {
    phone: "Phone",
    telegram: "Telegram",
    whatsapp: "WhatsApp",
    email: "Email",
  },

  consent: {
    unknown: "Consent unknown",
    opted_in: "Opted in",
    opted_out: "Opted out",
  },

  // ---- §2.20 CUSTOMER NOTIFICATIONS (P3-E PR-E1, D-291). ONE vocabulary for
  // the two surfaces that list what a customer was told: §4.7's panel on the
  // order and the composer's Notifications card on the quote. A row's `kind`,
  // `status` and `failed_reason` are codes on the wire; these are their words,
  // so neither surface ever prints a code.
  notification: {
    colKind: "Message",
    colChannel: "Channel",
    colStatus: "Delivery",
    // The row's `created_at` — when the message was written, which for a
    // queued or held row is before anything went out. The column used to say
    // "Sent", which was a lie on exactly those rows.
    colWhen: "When",
    kind: {
      received: "Request received",
      order_confirmed: "Order confirmed",
      order_in_production: "Order in production",
      order_ready: "Order ready",
      order_shipped: "Order shipped",
      order_cancelled: "Order cancelled",
      quote_sent: "Quote sent",
      quote_expiring: "Quote about to expire",
      ready_not_collected: "Reminder: ready for collection",
      quote_follow_up: "Quote reminder",
    },
    state: {
      queued: "Sending…",
      held: "Held",
      sent: "Sent",
      delivered: "Delivered",
      failed: "Failed",
      skipped_no_channel: "Not sent: no way to reach them",
      skipped_opted_out: "Not sent: opted out",
      skipped_superseded: "Not sent: out of date",
      // D-317: the customer was archived before the message went out.
      skipped_customer_archived: "Not sent: customer archived",
    },
    // A HELD row's release instant, in the TENANT's clock (D-291 ruling 3:
    // quiet hours are the tenant's `default_timezone`). The time is left out
    // rather than shown in the reader's own clock while the zone is unknown.
    heldUntil: "Held until {{when}} (quiet hours).",
    heldUntilUnknown: "Held until the quiet hours end.",
    // A QUEUED row with `send_after` is waiting out a retry after a failed
    // attempt (D-291 ruling 6).
    retryAt: "Trying again at {{when}}.",
    retryScheduled: "Another attempt is scheduled.",
    // `skipped_superseded` has three sources, and this sentence must be true
    // of each: a newer status push that went at a quiet-hours release, the
    // order moving on before the push could be sent, and the quote itself
    // overtaking a request acknowledgment. It used to name the quiet hours
    // alone, which was false for the other two.
    superseded: "A later update made it out of date, so it was not sent.",
    noChannel:
      "This customer has no Telegram or WhatsApp linked. Link one on their Contacts & channels tab so the next update reaches them.",
    // `failed_reason` — a stable code (D-225). `other` covers any code this
    // screen has no sentence for; the code itself stays on the row's
    // `data-reason` for support, never in the text.
    reason: {
      TEMPLATE_NOT_FOUND:
        "Its message template is missing, switched off, or has no version for this messenger. Check Message templates.",
      TEMPLATE_VARS_INVALID: "Its message template asks for details this message does not carry.",
      SEND_ERROR: "It could not be passed on for sending.",
      DELIVERY_FAILED: "The messenger did not deliver it.",
      other: "It could not be sent.",
    },
  },

  offline: {
    title: "You are offline",
    body: "You're offline. This page reloads when you're back.",
  },

  // ---- §4.1 customer list ------------------------------------------------
  customers: {
    title: "Customers",
    new: "New customer",
    // NOT bare "Search": the topbar carries a global search and the certified
    // walkthroughs address controls by exact accessible name (nav.searchLabel
    // records the same rule).
    search: "Search customers",
    // `q` matches a phone or a channel address only for a holder of
    // `messaging.address.read`; for anyone else the address leg is left out
    // of the query (spec 09 §4.1/§7, D-307). The screen picks the hint by that
    // grant, so a reader is never offered a match that cannot come.
    searchHintWithPhone: "Name, number, tax ID or phone",
    searchHintNoPhone: "Name, number or tax ID",
    filterTier: "Price tier",
    filterStatus: "Status",
    filterOwner: "Owner",
    filterCreditHold: "Credit hold",
    anyTier: "Any tier",
    anyStatus: "Any status",
    anyOwner: "Any owner",
    anyCreditHold: "Hold: any",
    creditHoldOn: "On hold",
    creditHoldOff: "Not on hold",
    statusActive: "Active",
    statusArchived: "Archived",
    colName: "Customer",
    colNo: "Number",
    colTier: "Tier",
    colOwner: "Owner",
    colReliability: "Reliability",
    colOpenAr: "Open AR",
    colCredit: "Credit",
    // Wave 1b (D-242): Created carries the default `-created_at` sort's
    // aria-sort on first paint and the way back to newest-first. Updated
    // (PR-E4, spec 09 §4.1) is hidden by default and shown through the
    // column chooser, with a caret on `updated_at`.
    colCreated: "Created",
    colUpdated: "Updated",
    // The caret buttons' accessible names — the screen passes the finished
    // sentence per column and per next-direction; the primitive interpolates
    // nothing. Two whole templates rather than a glued-on direction word,
    // because Russian declines what English appends.
    sortAsc: "Sort by “{{column}}”, ascending",
    sortDesc: "Sort by “{{column}}”, descending",
    // §3.9 — the score is a stand-in until module 11 supplies payment history.
    provisional: "provisional",
    // §3.5b — a typed degradation said in the reader's language (D-225), never
    // a zero pretending to be a balance.
    arUnavailable: "No receivables data",
    creditHold: "Credit hold",
    prepayOnly: "Prepay only",
    currencyWarning:
      "No active price list in {{currency}} — pricing will fail until one is activated.",
    warningUnknown: "This customer has a warning this screen cannot name yet.",
    // §4.1's open-orders count needs the order table P3-C ships. Stated, not
    // faked as a zero (D-033).
    // No longer "newest first": the reader now chooses the order, and the
    // th's aria-sort states it — a caption restating one order would be
    // wrong under every other.
    tableCaption: "Customers",
    emptyTitle: "No customers yet",
    emptyBody: "Create one, or import them from the onboarding wizard.",
    // The book is empty only under the filters in force (the reader's own or
    // a saved view's), so the act it asks for is widening them.
    emptyFilteredTitle: "No customers match these filters",
    emptyFilteredBody: "Widen the search, tier, status, owner, credit hold, next step or tag.",
    error: "Could not load the customers",
    // E2-1 (spec 09 §4.1, D-313 rulings 6 and 7): the Next step column,
    // visible by default, shows the next step's date or the flag's chip
    // (`sales.nextStep.*`), and the filter is `needs_next_step` in the URL.
    colNextStep: "Next step",
    filterNeedsNextStep: "Needs a next step",
    // E2-2a1 (spec 09 §4.1, D-323 rulings 5 to 7): the Tag filter, the Tags
    // column and the selection. A tag is the tenant's own word, so the
    // option shows its name; an archived tag that still filters says so.
    filterTag: "Tag",
    anyTag: "Any tag",
    tagArchivedOption: "{{name}} (archived)",
    colTags: "Tags",
    selectAll: "Select the customers on this page",
    selectRow: "Select {{customer}}",
    // E2-2c's duplicate warning on a create's 201, shipped here so the
    // header's list parser never meets a code it cannot say (§3.13).
    warningPossibleDuplicate:
      "A customer with a similar name or the same tax ID may already exist. Check the book before you quote.",
    // §4.1's E2-2c annotation: the page head's door into §4.18.
    import: "Import customers",
  },

  // E2-2a1 (spec 09 §4.2, D-323 rulings 1 to 4): the 360's pinned note and
  // its Notes panel. A note is what the next person on the account must know.
  // The server's redaction marker is never painted; the note says who removed
  // the text and when, in the reader's language.
  notes: {
    title: "Notes",
    pinnedLabel: "Pinned note",
    pinned: "Pinned",
    carriedOver: "carried over",
    byOn: "{{name}}, {{date}}",
    formerMember: "A former member",
    redactedBy: "Text removed by {{name}} on {{date}}",
    redactedOn: "Text removed on {{date}}",
    addLabel: "New note",
    addHint: "What the next person on this account must know",
    count: "Characters: {{count}} of {{max}}",
    pinOnAdd: "Pin this note",
    add: "Add note",
    adding: "Adding…",
    pin: "Pin",
    unpin: "Unpin",
    redact: "Redact",
    redactConfirm:
      "Remove this note's text for everyone? The words cannot come back. The note keeps its author, its date and its pin.",
    redactYes: "Remove the text",
    empty: "No notes yet",
    error: "Could not load the notes",
    loading: "Loading the notes…",
    readOffline: "The network is gone, so the notes show when it returns.",
    moreFailed: "Could not load the older notes. Try Show more again.",
    writeFailed: "Could not save the note. Your text is kept.",
    verbFailed: "Could not change the note.",
    offline: "The network is gone. Your text is kept, and Add returns with the network.",
    archivedReadOnly: "This customer is archived, so it takes no new notes and no pins.",
    refusal: {
      CUSTOMER_ARCHIVED: "This customer is archived, so it takes no new notes and no pins.",
      NOTE_NOT_FOUND: "That note is no longer one you can see.",
      CUSTOMER_NOT_FOUND: "This customer is no longer one you can see.",
      forbidden: "Your role cannot change this customer's notes.",
    },
  },

  // E2-2a2 (spec 09 §2.29, §4.2, §4.4 and §4.7; D-323 rulings 8 to 14, D-335
  // rulings 1 and 2): the Attached files panel on a customer's Documents tab,
  // a saved quote and an order. A file type reads as the format's own
  // abbreviation from code (`typeLabel`), never from here.
  attachments: {
    title: "Attached files",
    showDetached: "Show detached files",
    loading: "Loading the attached files…",
    error: "Could not load the attached files",
    readOffline: "The network is gone, so the attached files show when it returns.",
    empty: "No files attached yet.",
    emptyAll: "No files have been attached here.",
    moreFailed: "Could not load the older files. Try Show more again.",
    // §7 narrowing 19: a reader without the value grant is told how many
    // commercial files the list left out, never which.
    withheld:
      "Files not shown to your role: {{count}}. They hold commercial terms, which your role does not see.",
    untitled: "Untitled {{type}}",
    byOn: "{{name}} on {{date}}",
    on: "Attached on {{date}}",
    detachedChip: "Detached",
    download: "Download",
    downloading: "Opening…",
    downloadFailed: "Could not open the file.",
    detach: "Detach",
    detaching: "Detaching…",
    detachFailed: "Could not detach the file.",
    detachTitle: "Detach this file?",
    detachBody:
      "{{name}} leaves the list of attached files. The file stays on record, still downloads under Show detached files, and cannot be attached again.",
    detachYes: "Detach the file",
    customerArchived: "This customer is archived, so it takes no new files.",
    col: {
      title: "File",
      category: "Category",
      type: "Type",
      size: "Size",
      attached: "Attached",
      actions: "Actions",
    },
    category: {
      technical: "Technical",
      commercial: "Commercial",
    },
    categoryHint: {
      technical:
        "Technical files, such as drawings and certificates, are shown to everyone who can open this record, the warehouse and quality included.",
      commercial:
        "Commercial files, such as contracts and price agreements, are shown only to people who can see prices.",
    },
    // Refused in the browser before a byte is read, with the sentence the
    // server's own refusal would earn.
    problem: {
      type: "This file type isn't accepted. Use PDF, PNG, JPEG, XLSX, DOCX, PPTX, CSV, TXT or DWG.",
      empty: "This file is empty, so there is nothing to attach.",
      too_large: "This file is larger than {{max}}, the most a file can be.",
    },
    upload: {
      choose: "Choose a file",
      chooseOther: "Choose another file",
      types: "PDF, PNG, JPEG, XLSX, DOCX, PPTX, CSV, TXT or DWG, up to {{max}}.",
      category: "Category",
      title: "Title (optional)",
      titleHint: "What this file is, for example: Supply contract 2026",
      titleLong: "A title is at most {{max}} characters.",
      hiddenAfter:
        "Your role does not see commercial files, so this file will not show in the list once it is attached.",
      submit: "Upload",
      busy: "Uploading…",
      progressLabel: "Upload progress",
      offline: "The network is gone, so Upload returns with the network.",
      failed: "Could not attach the file.",
      transferFailed:
        "The file did not finish uploading. Check the network and press Upload again, and it continues where it stopped.",
      done: "The file is attached.",
      phase: {
        hashing: "Preparing the file…",
        creating: "Starting the upload…",
        uploading: "Uploading: {{percent}}%",
        storing: "Checking the file's contents…",
        attaching: "Attaching the file…",
      },
    },
    refusal: {
      FILE_INTEGRITY_MISMATCH:
        "This file's contents are not what its name says, so it was not attached. Check that it is the file you meant to send.",
      FILE_CONTENT_TYPE_NOT_ALLOWED:
        "This type of file is not accepted, and archives such as ZIP are not accepted either.",
      FILE_TOO_LARGE: "This file is larger than the most a file can be.",
      FILE_NOT_STORED:
        "The upload did not finish, so the file was not attached. Press Upload again.",
      ATTACHMENT_FILE_NOT_YOURS: "Only the person who uploaded this file can attach it.",
      ATTACHMENT_ALREADY_LINKED:
        "This file is already attached. Choose the file again to attach a new copy.",
      ATTACHMENT_FILE_WRONG_PURPOSE:
        "This file was uploaded for something else, so it cannot be attached here.",
      ATTACHMENT_NOT_FOUND: "This file is no longer one you can open, so the list was read again.",
      CUSTOMER_ARCHIVED: "The customer is archived, so it takes no new files.",
      CUSTOMER_NOT_FOUND: "This customer is no longer one you can see.",
      QUOTE_NOT_FOUND: "This quote is no longer one you can see.",
      ORDER_NOT_FOUND: "This order is no longer one you can see.",
      CUSTOMER_MERGED:
        "This customer was merged into {{name}}, so its files are attached there now.",
      forbidden: "Your role cannot change the files attached here.",
    },
  },

  // E2-2a2 (spec 09 §4.2, D-323 ruling 14): the 360's Documents tab, the
  // customer's quotes and orders as one timeline.
  documentsTab: {
    title: "Documents",
    loading: "Loading the documents…",
    error: "Could not load this customer's quotes and orders",
    readOffline: "The network is gone, so the documents show when it returns.",
    empty: "No quotes or orders for this customer yet.",
    moreFailed: "Could not load the older documents. Try Show more again.",
    col: {
      number: "Number",
      kind: "Document",
      status: "Status",
      total: "Total",
      created: "Created",
      actions: "Actions",
    },
    kind: {
      quote: "Quote",
      order: "Order",
    },
    quotePdf: "Quote (PDF)",
    proforma: "Proforma invoice (PDF)",
    shipments: "Shipments",
    hideShipments: "Hide shipments",
    shipmentsTitle: "Shipments",
    shipmentsEmpty: "No shipments for this order yet.",
    shipmentsError: "Could not load this order's shipments.",
  },

  // E2-2a1 (spec 09 §4.1, §4.2 and §4.13, D-323 ruling 5): the tenant's tags.
  // Every tag renders as the same neutral chip, so the name carries the
  // meaning.
  tags: {
    title: "Tags",
    none: "No tags",
    archivedSuffix: "archived",
    add: "Add tag",
    addLabel: "Tag to add",
    choose: "Choose a tag",
    remove: "Remove the tag {{name}}",
    noneToAdd: "No other active tag to add",
    optionsFailed: "Could not load the tags",
    verbFailed: "Could not change the customer's tags.",
    refusal: {
      TAG_ARCHIVED: "That tag is archived, so it cannot be added.",
      TAG_NOT_FOUND: "That tag no longer exists.",
      CUSTOMER_ARCHIVED: "This customer is archived, so its tags do not change.",
      forbidden: "Your role cannot change this customer's tags.",
    },
    cardBody:
      "Tags group customers for the Tag filter, bulk actions and export. An archived tag stays on its customers.",
    colName: "Tag",
    colStatus: "Status",
    colActions: "Actions",
    statusActive: "Active",
    statusArchived: "Archived",
    newLabel: "New tag name",
    new: "New tag",
    creating: "Creating…",
    rename: "Rename",
    renameLabel: "New name for {{name}}",
    archive: "Archive",
    archiving: "Archiving…",
    archiveConfirm:
      "Archive {{name}}? It leaves every picker and cannot be made active again. Customers keep it.",
    showArchived: "Show archived",
    emptyTitle: "No tags yet",
    emptyBodyCurator: "Create the first one, such as Dealer or Key account.",
    emptyBodyReader: "An admin creates the tags here.",
    emptyActiveTitle: "No active tags",
    emptyActiveBodyCurator: "Create one, or tick Show archived to see the archived tags.",
    emptyActiveBodyReader: "Tick Show archived to see the archived tags.",
    error: "Could not load the tags",
    refused: "Tags are not shown to your role",
    readOnly: "Only an admin changes the tag list.",
    nameTaken: "Another active tag already has this name.",
    limitReached: "The workspace holds 200 active tags. Archive one before you create another.",
    nameInvalid: "A tag name is 1 to 40 characters and holds no semicolon.",
    saveFailed: "Could not save the tag.",
    loading: "Loading the tags…",
    caption: "Tags",
  },

  // E2-2a1 (spec 09 §4.1, D-323 ruling 7): the customer book's bulk bar. One
  // request per selected row; the outcome line counts each result and names
  // each refusal. It never claims "all N matching": a page cannot know N.
  bulk: {
    selected: "Selected: {{count}}",
    clear: "Clear the selection",
    tagLabel: "Tag",
    ownerLabel: "New owner",
    chooseTag: "Choose a tag",
    chooseOwner: "Choose an owner",
    addTag: "Add tag",
    removeTag: "Remove tag",
    reassign: "Reassign owner",
    archive: "Archive",
    archiveConfirm:
      "Archive the selected customers ({{count}})? An archived customer takes no new quote, order or deal.",
    archiveYes: "Archive them",
    liveBusiness:
      "Selected customers with an open quote, order or deal: {{count}}. Archive will refuse them.",
    liveBusinessChip: "Open business",
    offline: "The network is gone, so the bar waits until it returns.",
    working: "Working: {{done}} of {{total}}",
    outcomeDone: "Done: {{count}}",
    // A row whose state already answered the verb: nothing was sent, or the
    // server said the change was already made. Worded per verb, so a reassign
    // reads "already the owner" (spec 09 §10 M09-S27 step 7).
    already: {
      add_tag: "Already tagged: {{count}}",
      remove_tag: "Already without the tag: {{count}}",
      reassign_owner: "Already the owner: {{count}}",
      archive: "Already archived: {{count}}",
    },
    outcomeRefused: "Refused, {{reason}}: {{count}}",
    outcomeFailed: "Failed, can be retried: {{count}}",
    outcomePending: "Not sent: {{count}}",
    retryFailed: "Retry failed",
    verb: {
      add_tag: "Add tag",
      remove_tag: "Remove tag",
      reassign_owner: "Reassign owner",
      archive: "Archive",
    },
    reason: {
      CUSTOMER_ARCHIVED: "customer archived",
      CUSTOMER_HAS_OPEN_DOCS: "open quote, order or deal",
      CUSTOMER_NOT_FOUND: "customer not visible to you",
      TAG_ARCHIVED: "tag archived",
      TAG_NOT_FOUND: "tag no longer exists",
      OWNER_CHANGED: "owner changed meanwhile",
      OWNER_NOT_MEMBER: "not an active member",
      PERMISSION_DENIED: "not allowed for your role",
      VALIDATION_FAILED: "request not accepted",
    },
    reasonOther: "code {{code}}",
  },

  create: {
    title: "New customer",
    legalName: "Legal name",
    displayName: "Display name",
    tier: "Price tier",
    currency: "Currency",
    currencyHint: "Leave empty for the company default",
    taxId: "Tax ID",
    submit: "Create customer",
    creating: "Creating…",
    failed: "Could not create the customer.",
  },

  // ---- §4.1 the candidates panel (P3-E2 PR E2-2c, §3.13, D-324) ----------
  // It warns and never blocks: every sentence here says what was found and
  // leaves the create to the reader. A failed or throttled check is one muted
  // line, never an error card, because the create does not depend on it.
  duplicates: {
    title: "Similar customers already in the book",
    checking: "Checking for similar customers…",
    hint: "You can still create this customer.",
    open: "Open",
    openLabel: "Open {{customer}} in a new tab",
    matchTaxId: "Same tax ID",
    matchDisplayName: "Similar name",
    matchLegalName: "Similar legal name",
    hidden: "{{count}} similar customers you cannot open",
    hidden_one: "{{count}} similar customer you cannot open",
    failed: "Could not check for similar customers. You can still create this one.",
  },

  // ---- §4.18 import customers (P3-E2 PR E2-2c, D-325) --------------------
  // The page wraps module 02's import flow, whose own words live under
  // `onboarding.importShared`, `onboarding.catalogueStep` and
  // `onboarding.importDetail`; these are only the page's frame.
  // §4.19 import orders: the open orders lane in sales.
  orderImport: {
    open: "Import orders",
    title: "Import open orders",
    intro:
      "One row per order line, grouped by your order number. Orders load as confirmed at the file's prices, and the customer is not told.",
    back: "Back to orders",
    allImports: "All order imports",
    jobTitle: "Import file",
    otherKind: "This import is not an order import. Open it from the setup wizard.",
    loading: "Loading…",
  },

  customerImport: {
    title: "Import customers",
    intro:
      "Every row is checked before anything is saved. You choose whether rows with errors are skipped.",
    back: "Back to customers",
    allImports: "All customer imports",
    jobTitle: "Import file",
    // A job of another lane opened at this page's address (a pasted id).
    otherKind: "This import is not a customer import. Open it from the setup wizard.",
    loading: "Loading…",
    roleError: "Could not check what your role may do here.",
  },

  // ---- §4.17 merge, §3.14 restore and the tombstone (P3-E2 PR E2-2b) ------
  // The customer in the address STAYS (the winner) and the one picked is
  // MERGED IN (the loser). The screen never says "winner" or "loser": a sales
  // office says which record stays. Every refusal says what happened and what
  // to do next (§4.17); none prints a raw code.
  merge: {
    // The 360's verbs and the tombstone (§4.2's E2-2b annotation).
    open: "Merge",
    narrow: "Merging two customers needs a wider screen",
    restore: "Restore customer",
    restoring: "Restoring…",
    restoreFailed: "Could not restore the customer.",
    restoreMerged:
      "This customer was merged into {{name}}, so it cannot be restored. Work on {{name}} instead.",
    anotherCustomer: "another customer",
    tombstoneLead: "Merged into",
    tombstoneOn: "on {{date}}",
    // The book's archived row (§4.1's E2-2b annotation).
    listMergedInto: "Merged into",
    // §4.17 itself.
    title: "Merge customers",
    intro: "{{name}} stays. The customer you merge in is archived and points here.",
    forbidden: "Merging customers is for group and facility admins.",
    winnerArchived:
      "This customer is archived, so nothing can be merged into it. Restore it first or open the customer it was merged into.",
    winnerError: "Could not load the customer",
    pickTitle: "The customer to merge in",
    pickLabel: "Find a customer",
    pickHint: "Name, number or tax ID",
    pickChoose: "Choose a customer",
    pickShort: "Type at least two letters to search.",
    pickNone: "No other active customer matches.",
    pickFailed: "Could not search the customers. Try again.",
    pickAnother: "Pick another customer",
    previewLoading: "Reading what the merge would do…",
    previewError: "Could not read the merge preview",
    previewNotFound: "That customer is not one you can open. Pick another one.",
    previewSame: "A customer cannot be merged into itself. Pick another one.",
    stays: "Stays",
    mergedIn: "Merged in",
    fieldsTitle: "Which value each field keeps",
    colField: "Field",
    keepFrom: "Keep {{field}} from {{name}}",
    field: {
      legal_name: "Legal name",
      display_name: "Display name",
      tax_id: "Tax ID",
      country: "Country",
      tier: "Price tier",
      default_currency: "Currency",
      credit_limit_minor: "Credit limit",
      credit_terms_days: "Payment terms",
      credit_hold: "Credit hold",
      billing_address: "Billing address",
      shipping_address: "Shipping address",
      preferred_channel: "Preferred channel",
      preferred_locale: "Preferred language",
    },
    notRecorded: "Not recorded",
    holdOn: "On hold",
    holdOff: "Not on hold",
    channelNone: "No preference",
    currencyLinked:
      "The two customers use different currencies, so the currency and the credit limit are kept from the same customer.",
    tierLocked:
      "Your role cannot change a price tier, so the tier of the customer that stays is kept.",
    taxIdRefused:
      "This tax ID does not fit the chosen country. Keep the other tax ID or the other country, then merge again.",
    rowsTitle: "What moves and what stays",
    colKind: "Records",
    colMoves: "Move",
    colStays: "Stay",
    colWhy: "Why they stay",
    kind: {
      customer_contacts: "Contacts",
      contact_channels: "Contact channels",
      customer_notes: "Notes",
      customer_tag_links: "Tags",
      quotes: "Quotes",
      sales_orders: "Orders",
      opportunities: "Deals",
      sales_activities: "Tasks",
      customer_notifications: "Notifications",
      customer_price_overrides: "Negotiated prices",
      customer_distributor_products: "Distributor products",
      customer_assignments: "Collaborators",
      // E2-2a2 (§2.33): the customer's own attached files, detached ones
      // included, move with the record.
      sales_attachments: "Attached files",
      // K-3 (D-546 ruling 3): the quote send records §9.17's guard counts
      // move with the customer, so the winner inherits the loser's sends.
      quote_sends: "Quote send records",
      customer_reliability_snapshots: "Reliability scores",
    },
    stayWhy: {
      customer_notifications:
        "Notifications already sent or settled stay with the record they were sent from. Unsent ones move.",
      customer_price_overrides:
        "They overlap a negotiated price of the customer that stays, or they are in another currency.",
      customer_distributor_products:
        "The customer that stays is not a distributor, or already lists the product.",
      customer_reliability_snapshots: "A score stays with the record it was measured on.",
    },
    tierRecount:
      "Distributor products are counted for the current price tier. The merge counts them again for the tier you keep.",
    accessTitle: "Who gains access",
    accessBody:
      "These people work the merged-in customer and become collaborators on the customer that stays. Clear a box to leave someone out.",
    accessNone: "Nobody gains access.",
    accessKeep: "Give {{name}} access",
    filesTitle: "Commercial files whose readers change",
    filesBody:
      "These files follow their customer, quote or order, so the people who work the customer that stays will read them.",
    filesNone: "No commercial file changes readers.",
    filesUntitled: "Untitled file",
    filesWithheld:
      "Commercial files whose readers change: {{count}}. Your role is not shown their names.",
    blockersTitle: "What stops this merge",
    blocker: {
      MERGE_TAX_ID_CONFLICT:
        "The two customers carry different tax IDs, so they are two companies. Correct the wrong tax ID on its customer first.",
      MERGE_CURRENCY_MISMATCH:
        "The customers use different currencies, and the merged-in customer has quotes, orders or deals that keep their own currency. These two cannot be merged.",
      MERGE_IMPORT_ORDER_REF_CONFLICT:
        "Both customers have a loaded order with the same number. Cancel one of each pair first:",
    },
    importConflict: "{{ref}}: {{loser}} on the customer merged in, {{winner}} on this one",
    blockersHint: "Merge stays off until nothing stands here.",
    confirm: "Merge into {{name}}",
    confirming: "Merging…",
    offline: "You are offline. Merge comes back when the connection does.",
    staleTitle: "Something changed since you read the preview, so it was read again:",
    stale: {
      winner: "the customer that stays was edited",
      loser: "the merged-in customer was edited",
      access: "the people who gain access changed",
      files: "the commercial files changed",
    },
    staleHint: "Check the preview below and merge again.",
    busyRetrying: "Another change was holding these customers. Trying again…",
    busy: "The customers are still busy with another change. Wait a moment, then press Merge again.",
    failed: "Could not merge the customers.",
    refused: {
      MERGE_CUSTOMER_ARCHIVED:
        "One of the two customers was archived since the preview. Open it to see why, then start again.",
      MERGE_SAME_CUSTOMER: "A customer cannot be merged into itself. Pick another one.",
      tier: "Your role cannot change a price tier. Keep the tier of the customer that stays.",
      notFound: "One of the two customers is no longer one you can open. Pick another one.",
      forbidden: "Your role cannot merge customers.",
    },
  },

  // ---- §4.2 customer 360 -------------------------------------------------
  c360: {
    crumbRoot: "Customers",
    error: "Could not load the customer",
    creditLimit: "Credit limit",
    creditTerms: "Payment terms",
    termsDays: "{{count}} days",
    // §2.1's legal name, the header's first fact (E2-2b; M09-S29 step 3).
    legalName: "Legal name",
    // §10.28's M09-S30 step 3 reads the customer's preferred language on the
    // 360 (§2.1's `preferred_locale`, which the documents print in and the
    // customers import lane updates). Named in the reader's language.
    preferredLanguage: "Preferred language",
    localeName: {
      en: "English",
      ru: "Russian",
      zh: "Chinese",
      fr: "French",
    },
    creditHold: "Credit hold",
    creditFailed: "Could not save the credit change.",
    archive: "Archive customer",
    archiving: "Archiving…",
    archiveFailed: "Could not archive the customer.",
    formula: "How is this scored?",
    reliabilityFormula: "{{onTime}} of {{paid}} invoices on time → {{score}}",
    reliabilityNone: "Not scored yet",
    tabsAria: "Customer sections",
    tabs: {
      overview: "Overview",
      contacts: "Contacts & channels",
      pricing: "Pricing",
      // E2-2a2 (spec 09 §4.2): the quotes, orders and attached files.
      documents: "Documents",
    },
    overview: {
      arTitle: "Open receivables",
      contactTitle: "Who to call",
      noPrimary: "No primary contact yet",
      preferredChannel: "Preferred channel",
      owner: "Owner",
      // "Latest", not "Open": the panel shows the most recent quotes WITH
      // their status chips, because `GET /v1/quotes` filters one status at a
      // time and "open" is a set of four. A panel headed "Open quotes" that
      // listed a rejected one would be lying about its own contents.
      quotesTitle: "Latest quotes",
      quotesEmpty: "No quotes for this customer yet.",
      quotesMore: "All quotes for this customer",
      quotesError: "Could not load this customer's quotes",
      // E2-1 (spec 09 §4.2, D-313): three more panels, each read on its own
      // with its own one-line failure, so one refused read never blanks the
      // page. The open tasks use `sales.tasks.card*` for their rows.
      tasksTitle: "Open tasks",
      tasksEmpty: "No open tasks for this customer.",
      tasksError: "Could not load the open tasks",
      dealsTitle: "Open deals",
      dealsEmpty: "No open deals for this customer.",
      dealsError: "Could not load this customer's deals",
      dealsMore: "All deals for this customer",
      ordersTitle: "Latest orders",
      ordersEmpty: "No orders for this customer yet.",
      ordersError: "Could not load this customer's orders",
      ordersMore: "All orders for this customer",
    },
    // E2-1: the header's next step, drawn from `sales.nextStep.*`.
    nextStep: "Next step",
    pricing: {
      effectiveTitle: "Effective price list",
      noEffectiveList: "No active price list in {{currency}} for this tier.",
    },
    // The §2.2 assignment panel (D-241: the collaborator endpoints are a
    // flagged §5 omission — `role='collaborator'` exists in §2.2 and §3.4
    // grants collaborators send rights, but no route offered it).
    assignments: {
      title: "Who works this customer",
      body: "The owner and collaborators can quote this customer and send those quotes.",
      roleOwner: "Owner",
      roleCollaborator: "Collaborator",
      add: "Add a colleague",
      addTitle: "Add to this customer",
      member: "Colleague",
      role: "Role",
      remove: "Remove",
      removing: "Removing…",
      empty: "Nobody is assigned yet.",
      // Disclosed BEFORE the press, never as a 409 after it.
      lastOwnerGuard:
        "This is the only owner. Assign another owner first — a customer is never left without one.",
      ownerSwap:
        "Making somebody else the owner moves the current owner to collaborator; they keep their quotes.",
      addFailed: "Could not add the assignment.",
      removeFailed: "Could not remove the assignment.",
      error: "Could not load the assignments",
    },
  },

  contacts: {
    add: "Add contact",
    addTitle: "Add contact",
    editTitle: "Edit contact",
    fullName: "Full name",
    position: "Position",
    primaryLabel: "Primary contact",
    primary: "Primary",
    primaryYes: "Yes",
    primaryNo: "No",
    archive: "Archive",
    restore: "Restore",
    archived: "Archived",
    // The one-way consequence, disclosed BEFORE the press (D-069).
    archiveHint:
      "Archiving clears the primary flag and deletes this contact's messaging identities.",
    none: "No contacts recorded yet.",
    noChannels: "No messaging identities",
    channelKind: "Channel",
    identity: "Identity",
    addChannel: "Add identity",
    remove: "Remove",
    verified: "Verified",
    unverified: "Not verified",
    createFailed: "Could not add the contact.",
    updateFailed: "Could not save the contact.",
    channelAddFailed: "Could not add the identity.",
    channelRemoveFailed: "Could not remove the identity.",
    // `telegramPending` is GONE, not outstanding: module 17's §4.5 link
    // widget landed on the contact cards (P3-B part 2), and its strings live
    // in the messaging namespace it belongs to.
  },

  // ---- §4.2 Pricing tab: the distributor eligible list -------------------
  eligible: {
    title: "Eligible products",
    skuLabel: "Product SKU",
    skuHint: "Type the exact SKU",
    skuApplied: "{{sku}} · {{name}}",
    skuNoMatch: "No product with that exact SKU",
    note: "Note",
    add: "Add product",
    confirm: "Add to eligible list",
    remove: "Remove",
    colProduct: "Product",
    colNote: "Note",
    colAdded: "Added",
    colActions: "Actions",
    tableCaption: "Products this distributor is priced at distributor rates for",
    empty: "No eligible products — every quote prices at B2B rates.",
    addFailed: "Could not add the product.",
    removeFailed: "Could not remove the product.",
  },

  // ---- §3.2 price check (the §4.2 Pricing tab panel) ---------------------
  priceCheck: {
    title: "Price check",
    product: "Product SKU",
    productHint: "Type the exact SKU",
    productApplied: "{{sku}} · {{name}}",
    productNoMatch: "No product with that exact SKU",
    qty: "Quantity",
    uom: "Unit",
    uomPick: "Pick a unit",
    date: "Wanted date",
    resolve: "Check price",
    resolving: "Checking…",
    price: "Unit price",
    listPrice: "List price",
    source: {
      tier_list: "Tier list",
      customer_override: "Customer override",
      offcut_class: "Off-cut discount",
      manual: "Manual price",
    },
    // §9.5 — the distributor fallback is a FACT about the eligible list, not
    // an error: this product is not on it, so it prices at B2B.
    fallbackChip: "b2b (not in eligible list)",
    // §4.4's rule for a user without `sales.margin.read`: the boolean, and
    // none of the numbers behind it.
    needsApproval: "Needs approval",
    marginPct: "Margin {{pct}}%",
    unitCost: "Cost {{cost}}",
    guardPct: "Guard {{pct}}%",
    // §9.13 — a fact about the price book, not a failure of this screen.
    notListed: "No list price for this product, quantity and date — a manual price is needed.",
    failed: "Could not check the price.",
    // D-301: the dry run's answer is prices, and this reader does not hold
    // `sales.read_value`. Said in place of the form, never as an error card.
    valueHidden: "Prices are not shown to your role, so the price check is not available.",
  },

  // ---- §2.9 negotiated prices (the §4.2 Pricing tab's panel; P3-C-2, D-263) --
  //
  // "Negotiated price", not "override", on the glass: what a sales manager
  // agreed with one customer for one product. `override` is the wire's word
  // (§2.9) and stays in the code. A PENDING one is said to be "not applied"
  // in as many words, because §3.2 step 2 ignores it and a manager who
  // proposed a price at 10:00 must not quote it at 10:05.
  overrides: {
    title: "Negotiated prices",
    // The second sentence is D-263's derived state, disclosed: the wire still
    // says `active` past `valid_to`, the chip does not.
    intro: "Agreed prices apply once approved, until they expire.",
    tableCaption: "Negotiated prices for this customer",
    filterStatus: "State",
    filterAll: "All states",
    status: {
      pending_approval: "Awaiting approval",
      active: "Active",
      expired: "Expired",
      revoked: "Revoked",
    },
    kind: {
      fixed_price: "Fixed price",
      discount_pct: "Discount",
    },
    approver: {
      facility_admin: "a facility admin",
      group_admin: "a group admin",
    },
    // The roles a refusal NAMES (D-263's `required_role` extension), as
    // nouns for a list — "facility admin, group admin".
    roleName: {
      facility_admin: "facility admin",
      group_admin: "group admin",
    },
    colProduct: "Product",
    colValue: "Price",
    colMinQty: "From quantity",
    colValidity: "Validity",
    colStatus: "State",
    colReason: "Reason",
    colActions: "Actions",
    perUnit: "{{price}} per {{uom}}",
    discountValue: "{{pct}}% off the tier price",
    // §7 — the figure was OMITTED for this role: a sentence where the number
    // would be, never a blank that reads as "nothing was negotiated".
    redacted: "Not shown to your role",
    anyQty: "Any quantity",
    fromQty: "from {{qty}} {{uom}}",
    fromQtyNoUom: "from {{qty}}",
    needs: "Needs {{approver}}",
    reviewDelta: "Short of the guard by {{amount}} per unit",
    // The dialog.
    propose: "Propose a price",
    proposeTitle: "Propose a negotiated price",
    proposing: "Proposing…",
    kindLabel: "Kind",
    value: "Unit price",
    currencyFact: "In {{currency}}, the customer's currency",
    currencyFactUom: "In {{currency}}, the customer's currency, per {{uom}}",
    pct: "Discount, %",
    pctHint: "Off the tier list price",
    minQty: "Applies from quantity",
    minQtyHint: "In the product's base unit; leave empty for any quantity",
    minQtyHintUom: "In {{uom}}; leave empty for any quantity",
    validFrom: "Valid from",
    validTo: "Valid to",
    validToHint: "Leave empty for open-ended",
    reason: "Reason",
    reasonHint: "Required — the reviewer's evidence and the audit trail's",
    validityOrder: "The end date cannot precede the start date.",
    // §9.13's shape, D-263's rule: a fact about the price book, not a failure
    // of the form — and it names its code, because the refusal is the same
    // for a fixed price as for a discount (§3.2 step 2 never runs without a
    // step-1 list row), so "propose a fixed price instead" would be wrong
    // advice. `{{code}}` is the wire's own `PRICE_NOT_LISTED`.
    notListed:
      "{{code}}: no price list of this tier carries the product on the start date. Add it to that list first.",
    createFailed: "Could not propose the price.",
    // What the 201 said. The figures ride the server's `review` and are
    // §7-omitted per role, so the sentence shrinks to what this reader may
    // see — never to a number invented for them.
    outcomeActive: "Active — the next quote for this customer prices from it.",
    outcomePending:
      "Awaiting approval by {{approver}} — the margin guard was not cleared. Not applied until approved.",
    outcomePendingDelta:
      "Awaiting approval by {{approver}} — short of the guard by {{amount}} per unit. Not applied until approved.",
    outcomePendingMargin:
      "Awaiting approval by {{approver}} — {{margin}}% against a {{guard}}% guard. Not applied until approved.",
    outcomePendingBreach:
      "Awaiting approval by {{approver}} — short of the guard by {{amount}} per unit ({{margin}}% against {{guard}}%). Not applied until approved.",
    // Revoke — facility_admin / group_admin only (D-263); the sales user is
    // told why the control is absent rather than shown one that 403s.
    revoke: "Revoke",
    revokeReason: "Reason",
    revokeConfirm: "Confirm revoke",
    revoking: "Revoking…",
    revokeNote:
      "Quotes already sent keep the price they were sent with; drafts reprice on their next edit.",
    revokeRoleNote: "Revoking a negotiated price is for a facility or group admin.",
    revokeFailed: "Could not revoke the price.",
    // The 403 that names its roles (D-263) — rendered from the structured
    // `required_role`, so a Russian screen says the roles in Russian.
    revokeDenied: "Revoking is for these roles: {{roles}}. Ask one of them.",
    empty: "No negotiated prices for this customer yet.",
    emptyFiltered: "No negotiated prices in this state.",
    error: "Could not load the negotiated prices.",
  },

  // ---- §4.3 price lists --------------------------------------------------
  priceLists: {
    title: "Price lists",
    new: "New price list",
    filterTier: "Tier",
    filterCurrency: "Currency",
    filterStatus: "Status",
    anyTier: "Any tier",
    anyStatus: "Any status",
    currencyHint: "e.g. KZT",
    colCode: "Code",
    colName: "Name",
    colTier: "Tier",
    colCurrency: "Currency",
    colVat: "VAT",
    colValidity: "Valid",
    colStatus: "Status",
    colActivated: "Activated",
    openEnded: "open-ended",
    notActivated: "Not activated",
    status: {
      draft: "Draft",
      active: "Active",
      archived: "Archived",
    },
    // D-234 — whether the item prices carry VAT.
    vat: {
      inclusive: "VAT incl.",
      exclusive: "VAT excl.",
    },
    tableCaption: "Price lists, newest first",
    emptyTitle: "No price lists yet",
    emptyBody: "Create a draft list for a tier and currency, then activate it.",
    error: "Could not load the price lists",
  },

  listCreate: {
    title: "New price list",
    code: "Code",
    name: "Name",
    tier: "Tier",
    currency: "Currency",
    vatMode: "VAT mode",
    vatModeDefault: "Default for this tier",
    validFrom: "Valid from",
    validTo: "Valid to",
    submit: "Create draft",
    failed: "Could not create the price list.",
  },

  priceListDetail: {
    crumbRoot: "Price lists",
    error: "Could not load the price list",
    activate: "Activate",
    activating: "Activating…",
    activateFailed: "Could not activate this list.",
    archive: "Archive",
    archiveFailed: "Could not archive this list.",
    // §2.7 — repricing is a NEW draft, which is what the validity ranges are
    // for. Said on the screen, so the 409 is never reached by a control.
    readOnlyNote: "Only drafts can be edited. To reprice, create a new draft list.",
    // The PUT replaces the item set — stated above the control that does it.
    wholesaleNote: "Saving replaces the whole item set: rows removed here are deleted.",
    colProduct: "Product",
    colUom: "Unit",
    colMinQty: "From qty",
    colPrice: "Unit price",
    colMargin: "Margin preview",
    colActions: "Actions",
    marginPct: "{{pct}}%",
    addSku: "Product SKU",
    addUom: "Unit",
    addMinQty: "From qty",
    addPrice: "Unit price",
    addRow: "Add row",
    removeRow: "Remove",
    skuApplied: "{{sku}} · {{name}}",
    skuNoMatch: "No product with that exact SKU",
    save: "Save items",
    saveFailed: "Could not save the items.",
    emptyItems: "No items yet — add rows or import a file.",
    tableCaption: "Price list items",
    reportTitle: "Import result",
    reportApplied: "{{count}} applied",
    reportDeleted: "{{count}} deleted",
    reportClean: "Every row was accepted.",
    // The row-rejection vocabulary: the server's four structured codes plus
    // the three the client can decide before sending anything (D-225 — a code,
    // localized here, never a server-minted sentence).
    rowCode: {
      PRODUCT_NOT_FOUND: "No product with that SKU",
      UOM_NOT_FOUND: "No such unit",
      UOM_NOT_CONVERTIBLE: "That unit cannot be converted to the product's base unit",
      DUPLICATE_BREAK: "Two rows give the same product, unit and quantity break",
      PRICE_INVALID: "The price is not a plain amount this currency can carry",
      MIN_QTY_INVALID: "The quantity break is not a plain number",
      ROW_INVALID: "The row is missing SKU, unit or price",
    },
    conflictTitle: "Another active list already covers these dates",
    conflictBody:
      "One tier and currency can have one active list at a time. Change this list's dates, or archive the conflicting one.",
    conflictOwn: "This list",
  },

  import: {
    title: "Import prices",
    // §4.3 says "Excel import"; the lane shipped is CSV/TSV file or paste,
    // which is what Excel's own clipboard and "Save as" produce. The spec
    // sentence is amended in the same PR rather than left to imply .xlsx.
    hint: "CSV or TSV with columns sku, uom, min_qty, price. Pasting from a spreadsheet works.",
    file: "Choose a file",
    paste: "Or paste rows",
    pasteHint: "sku\tuom\tmin_qty\tprice",
    run: "Import",
    running: "Importing…",
    lineN: "Line {{n}}",
    badHeader: "The first row must name the columns sku, uom and price.",
    emptyFile: "There are no rows below the header.",
  },

  // ---- §4.3 off-cut discount classes -------------------------------------
  offcut: {
    title: "Off-cut discounts",
    new: "New discount class",
    createTitle: "New discount class",
    editTitle: "Edit discount class",
    code: "Code",
    codeHint: "UPPER_SNAKE, e.g. OFFCUT_COMBO3",
    // One label for every locale's name field, with the language named in
    // itself (D-446).
    nameIn: "Name ({{language}})",
    minPieces: "Pieces from",
    maxPieces: "Pieces to",
    minLength: "Piece length from",
    maxLength: "Piece length to",
    discount: "Discount %",
    bypassLabel: "Bypasses the margin guard",
    bypassHint:
      "A class that bypasses the guard prices below the minimum margin without asking for approval.",
    archivedLabel: "Archived",
    colCode: "Code",
    colName: "Name",
    colPieces: "Pieces",
    colMinLength: "Length from",
    colMaxLength: "Length to",
    colDiscount: "Discount",
    colBypass: "Margin guard",
    colStatus: "Status",
    colActions: "Actions",
    pct: "{{pct}}%",
    bypasses: "Bypasses",
    guarded: "Guarded",
    // §7's edit cell is group_admin alone; the shared code cannot tell ga from
    // fa, so the screen says which of them the server will accept.
    gaNote: "Only group admins can change discount classes.",
    readOnly: "Discount classes are configured by group admins.",
    tableCaption: "Off-cut discount classes",
    emptyTitle: "No discount classes",
    emptyBody:
      "The vertical pack seeds OFFCUT_SINGLE and OFFCUT_COMBO2 — install it, or create a class here.",
    error: "Could not load the discount classes",
    createFailed: "Could not create the discount class.",
    updateFailed: "Could not save the discount class.",
    // D-337 ruling 6: `discount_pct` reaches only a holder of
    // `sales.read_value`, so a class without it cannot be edited here.
    ratesHidden:
      "The discount rates are not shown to your role, so existing classes cannot be edited here.",
  },

  // ---- §4.3 margin guards ------------------------------------------------
  guards: {
    title: "Margin guards",
    new: "New margin guard",
    createTitle: "New margin guard",
    scopeLabel: "Applies to",
    scope: {
      default: "Every product",
      product: "One product",
      family: "A product family",
    },
    basis: {
      standard: "Standard cost",
      moving_avg: "Moving average cost",
    },
    tenantDefault: "Company default",
    familyTarget: "Product family",
    product: "Product SKU",
    productHint: "Type the exact SKU",
    productApplied: "{{sku}} · {{name}}",
    productNoMatch: "No product with that exact SKU",
    minMargin: "Minimum margin %",
    pct: "{{pct}}%",
    // §2.11's stated degradation, said where the control is rather than
    // discovered as a 422 after typing a form.
    gaNote: "Only group admins can change margin guards.",
    archive: "Archive",
    restore: "Restore",
    colScope: "Applies to",
    colTarget: "Target",
    colMinMargin: "Minimum margin",
    colCostBasis: "Cost basis",
    colStatus: "Status",
    colActions: "Actions",
    tableCaption: "Margin guards",
    emptyTitle: "No margin guards",
    emptyBody: "Without a guard no price is flagged as a breach. Add a company-wide floor first.",
    error: "Could not load the margin guards",
    createFailed: "Could not create the margin guard.",
    updateFailed: "Could not save the margin guard.",
  },

  // ---- the QUOTE vocabulary (§2.12 status, §2.14 price_source, the ATP
  // traffic-light) — one place, so the list, the composer and the public page
  // cannot name the same fact three ways.
  quoteStatus: {
    draft: "Draft",
    pending_approval: "Awaiting approval",
    approved: "Approved",
    sent: "Sent",
    accepted: "Accepted",
    converted: "Ordered",
    rejected: "Declined",
    expired: "Expired",
    cancelled: "Cancelled",
  },

  priceSource: {
    tier_list: "Tier price",
    customer_override: "Customer price",
    offcut_class: "Off-cut −{{pct}}%",
    offcutPlain: "Off-cut discount",
    manual: "Typed by hand",
    // §2.14 `off_list` — the analytics fact, said at the moment it is created.
    offList: "Off list",
  },

  atp: {
    label: "Availability",
    green: "In stock",
    amber: "Partly in stock",
    red: "To be produced",
    unknown: "Not checked",
  },

  // ---- §4.5 quote list -----------------------------------------------------
  quotes: {
    title: "Quotes",
    new: "New quote",
    tableCaption: "Quotes",
    colNo: "Quote",
    colCustomer: "Customer",
    colTotal: "Total",
    colStatus: "Status",
    colViewed: "Opened",
    colValid: "Valid until",
    colOrigin: "From",
    colRevisions: "Version",
    revision: "v{{count}}",
    // The quote PDF (P3-D PR-D4, D-288): the document that went out with the
    // latest send, offered on a sent quote to a reader holding
    // `sales.read_value` — a priced document cannot be redacted.
    pdf: "Quote (PDF)",
    origin: {
      manual: "Manual",
      agent: "Agent",
      portal: "Portal",
    },
    // D-241 viewed-tracking: the chip between sent and accepted.
    viewedYes: "Opened",
    viewedNo: "Not opened",
    viewedOn: "Opened {{when}}",
    validDays: "Days left: {{count}}",
    validExpired: "Past its date",
    filterStatus: "Status",
    filterOwner: "Owner",
    filterCustomer: "Customer",
    filterOrigin: "From",
    filterOffList: "Off-list lines only",
    filterFrom: "Created from",
    filterTo: "Created to",
    filterAll: "All",
    filterCustomerHint: "Customer name or number",
    // Two whole templates, never a glued-on direction word — Russian declines
    // what English appends (the customers screen states the same rule).
    sortAsc: "Sort by “{{column}}”, ascending",
    sortDesc: "Sort by “{{column}}”, descending",
    colCreated: "Created",
    selectAll: "Select the quotes on this page",
    selectRow: "Select {{quote}}",
    // The bulk bar NEVER claims "all N matching" — cursor paging cannot know
    // what it has not loaded, so the count is of LOADED, selected rows.
    bulkSelected: "Selected: {{count}}",
    bulkClear: "Clear the selection",
    bulkRemind: "Send a reminder",
    bulkExpire: "Expire",
    bulkWorking: "Working…",
    // Said ABOVE the table, because the checkbox on a draft row is disabled
    // and a reader is owed the reason before they try to tick it.
    bulkSentOnly: "Only sent quotes can be reminded or expired.",
    bulkResult: "Done: {{done}}. Refused: {{failed}}.",
    bulkRemindFailed: "Could not send every reminder.",
    bulkExpireFailed: "Could not expire every quote.",
    emptyTitle: "No quotes yet",
    emptyBody: "Price a customer's request and the quote lands here.",
    emptyFilteredTitle: "No quotes match these filters",
    // The book holds seven filters, so the body names no kind: a list of
    // them would name filters the reader never set (spec 09 §4.5, D-305
    // amendment (t)).
    emptyFilteredBody: "Widen or clear these filters.",
    error: "Could not load the quotes",
    loading: "Loading the quotes…",
  },

  // ---- §4.4 quote composer -------------------------------------------------
  composer: {
    crumbRoot: "Quotes",
    newTitle: "New quote",
    editTitle: "Quote {{no}}",
    customer: "Customer",
    customerHint: "Name or customer number",
    customerRequired: "Pick a customer before adding lines.",
    contact: "Recipient",
    contactNone: "No contact chosen",
    validity: "Valid for (days)",
    noteToCustomer: "Note to the customer",
    internalNote: "Internal note",
    internalNoteHint: "The customer never sees this.",
    linesTitle: "Lines",
    addLine: "Add a line",
    product: "Product",
    productHint: "Exact SKU",
    productNoMatch: "No product with that exact SKU",
    qty: "Quantity",
    uom: "Unit",
    colProduct: "Product",
    colQty: "Quantity",
    colUnitPrice: "Unit price",
    colPromise: "Promise date",
    promiseDate: "Promise date for this line",
    promisePast: "The promise date cannot be before today.",
    promiseNone: "Not set",
    colDelivery: "Delivery",
    checkDate: "Check date",
    deliveryBy: "Delivery by {{date}}",
    deliveryQuoted: "Quoted",
    deliveryRemove: "Remove date",
    deliveryPriceReset: "The unit changed, so the typed price was cleared.",
    checkDateProductError: "Could not load the product to check its date",
    deliveryRefused: {
      not_found: "This date is no longer on file. Check the date again or remove it.",
      not_quoted: "This date is no longer quoted. Check the date again or remove it.",
      expired: "This date has expired. Check the date again or remove it.",
      product_mismatch:
        "This date was checked for another product. Check the date again or remove it.",
      qty_mismatch:
        "This date was checked for another quantity. Check the date again or remove it.",
    },
    colSource: "Price from",
    colAtp: "Availability",
    colMargin: "Margin",
    colTotal: "Line total",
    colActions: "Actions",
    removeLine: "Remove",
    listPrice: "List {{price}}",
    priceTyped: "Type a price",
    // What the line costs if nothing is typed — localized money, beside an
    // input that takes a bare decimal. Mirrors `listPrice` one line up.
    priceResolved: "Prices at {{price}}",
    linesIncomplete: "{{count}} line(s) need a valid quantity or price before this can be saved.",
    // …and WHICH, because a count on a twenty-line quote is a search task.
    // The PRODUCT leads: the grid prints no line number, so the position alone
    // would still leave the manager counting rows.
    lineDefectQty: "{{product}} (line {{line}}): the quantity is not a number.",
    lineDefectPrice: "{{product}} (line {{line}}): the price is not a number.",
    // A row whose product is not chosen yet has no label to lead with.
    lineDefectQtyNoProduct: "Line {{line}}: the quantity is not a number.",
    lineDefectPriceNoProduct: "Line {{line}}: the price is not a number.",
    totalsIncomplete:
      "{{count}} line(s) are not priced yet — this total is only the lines that are.",
    resolving: "Pricing…",
    resolveFailed: "Could not price this line.",
    notListed: "This product has no price on the customer's list — type a price.",
    // D-301: the dry run's answer is prices, and this reader does not hold
    // `sales.read_value`. A fact about the role, never a failure.
    priceHidden: "Prices are not shown to your role, so this line has no price preview.",
    // D-337 ruling 1: a reader without `sales.read_value` is served a saved
    // quote with no line prices and no totals. The totals block says so in
    // one sentence instead of summing absent prices to zero, and Save is held
    // while a negotiated (off-list) price it cannot see would be repriced.
    totalsHidden: "Totals are not shown to your role.",
    reviseValueHidden:
      "Saving would swap negotiated prices for list prices you can't see. Ask someone who sees prices to save it.",
    marginTitle: "Margin",
    marginPct: "{{pct}}%",
    marginGuard: "Guard {{pct}}%",
    marginBreached: "Under the guard",
    // §4.4: a sales user WITHOUT `sales.margin.read` sees only this flag —
    // never a masked number, never a dash (§7 omission).
    needsApproval: "Needs approval",
    subtotal: "Subtotal",
    tax: "VAT",
    total: "Total",
    vatInclusive: "Prices include VAT",
    vatExclusive: "VAT added on top",
    totalsPreview: "Totals are recalculated when the draft is saved.",
    /** An unsaved draft has no VAT rate to state — the rate arrives on the
     * saved revision — so the preview says so instead of printing a zero. */
    vatOnSave: "VAT is added when the draft is saved.",
    saveDraft: "Save draft",
    saving: "Saving…",
    send: "Send",
    sending: "Sending…",
    sendForApproval: "Send for approval",
    sendApprovalHint: "A line is under the margin guard, so sending asks for a decision first.",
    // ---- ORDER MODE (`document: "order"`, /sales/orders/new — P3-D PR-D1,
    // D-283). The same component with a different document: no validity, no
    // notes (sales_orders has no such column), a facility picker and a
    // customer-PO field, and ONE verb whose label says whether the press
    // confirms the order or parks it for a decision.
    newOrderTitle: "New order",
    facility: "Fulfil from",
    // The first option of the facility picker — value "" — omits
    // `fulfilling_facility_id`, which is the §2.16 derivation over the
    // caller's scope-filtered active pool.
    facilityDerived: "Choose automatically",
    // Stated in place of the picker when the tenant has ONE active facility
    // (module 06's rule: no select with nothing to choose).
    facilityDerivedNote: "The order will be fulfilled from the facility that holds its stock.",
    poRef: "Customer PO",
    placeOrder: "Place the order",
    placeOrderForApproval: "Place for approval",
    placing: "Placing…",
    orderApprovalHint:
      "A line is under the guard or off the list, so this order waits for a decision before it confirms.",
    orderFailed: "Could not place the order.",
    // §4.4's mobile rule, inherited verbatim: the order composer has no
    // phone verb at all (there is nothing to approve or send on a document
    // that does not exist yet), so the note says what the screen is for.
    orderMobileNote: "Placing an order needs a bigger screen.",
    // The two totals hints, re-worded for a press that is a placement rather
    // than a draft save — the quote wording would promise a save step the
    // order document does not have.
    totalsPreviewOrder: "Totals are recalculated when the order is placed.",
    vatOnPlace: "VAT is added when the order is placed.",
    duplicate: "Duplicate",
    duplicating: "Duplicating…",
    duplicateHint: "Copies the lines into a new draft and reprices them at today's lists.",
    revise: "Save as a new version",
    reviseHint:
      "This quote has been sent. Saving creates version {{next}} and the customer's old link stops working.",
    saveFailed: "Could not save the quote.",
    sendFailed: "Could not send the quote.",
    // 422 DOCUMENT_GLYPH_COVERAGE on the send (D-288 ruling 9): the PDF is
    // drawn inside the send, so a character the document fonts cannot print
    // stops it. Follows `sales.documents.glyphsNamed`, which lists them; this
    // says where they live, because the header and footer text is the
    // tenant's own and no field on the quote shows it.
    sendGlyphsSource:
      "They come from the quote or from the document header or footer in the sales settings. Replace them and send again.",
    duplicateFailed: "Could not duplicate the quote.",
    sentTitle: "Sent",
    sentBody: "The customer can accept it at this link.",
    pendingTitle: "Sent for approval",
    pendingBody: "It goes out as soon as the breach is decided.",
    // §3.3 off-cut suggestion panel
    offcutTitle: "Off-cut option",
    offcutOpen: "Off-cut options",
    offcutClose: "Close",
    offcutOption: "{{qty}} as {{pieces}} at −{{pct}}%",
    offcutWhere: "Zone {{location}}",
    offcutApply: "Use this",
    offcutApplied: "Off-cut option applied",
    offcutNone: "No off-cut combination covers this quantity.",
    // D-301 round 7: the server withheld the options (`offcut_options_withheld`)
    // because each one carries a price this reader is not shown.
    offcutValueHidden: "Prices are not shown to your role, so off-cut options are not offered.",
    // Amber with an empty list and no withholding: pieces on the shelves cover
    // the line, and no off-cut discount applies to them.
    offcutUndiscounted:
      "Off-cut pieces cover this quantity, but no off-cut discount applies to them.",
    offcutFailed: "Could not look up off-cut options.",
    offcutSaves: "Uses stock that is already cut instead of fresh cable.",
    // The agent-draft banner (origin='agent')
    agentTitle: "Drafted by the offer agent",
    agentBody: "Read it, change what you like, and send it yourself.",
    agentSource: "The customer wrote:",
    agentNoSource: "The source message is no longer available.",
    agentOrigin: "Agent draft",
    agentNote: "Agent's note",
    agentNoteFrame: "Written by the agent. Check it before you send.",
    // §4.4 mobile rule, asserted by the responsive net rather than implied.
    mobileNote: "Line editing needs a bigger screen. Here you can read, approve and send.",
    collaboratorsTitle: "Who can work this quote",
    // A LABEL, not a sentence with a hole in it: the name beside it is a
    // `RefLabel` node (a resolved name, or the register's short-id chip when
    // the reader may not read the member index), and a node cannot be
    // interpolated into a translated string.
    collaboratorsOwner: "Owner of this customer",
    collaboratorsWithLabel: "Working with",
    collaboratorsManage: "Change on the customer",
    emptyLinesTitle: "No lines yet",
    emptyLinesBody: "Add the first product and quantity.",
    error: "Could not load the quote",
    readOnly: "This quote can no longer be edited.",
    // D-268: an expired quote offers the version verb and nothing else — the
    // send route refuses it, so the screen says why rather than showing a
    // button that answers 409.
    expiredNote:
      "This quote has expired. Save it as a new version to reprice at today's lists and send it again.",
    // The same fact WITHOUT the remedy, for an expired quote whose customer no
    // longer admits new documents (task #229): the new version is refused too,
    // and the customer note below names what can be done about that.
    expiredNoteBlocked: "This quote has expired, so it can no longer be sent.",
    // §3.1.4 — task #229. Said where Save and Duplicate would otherwise be.
    // It states the fact and points to another customer, and advises no
    // restore: a customer restore is designed with P3-E2-2's merge (D-250),
    // since a merged-away customer must never be restorable (#277, R10).
    customerArchivedNote:
      "This customer is archived, so this quote can't be revised or copied. Start a new quote instead.",
    customerUnassignedNote:
      "This customer is no longer yours, so this quote can't be revised or copied. Ask an admin to reassign them.",
    // The `?customer_id=` link pointed at an archived customer (task #229):
    // the picker was left empty on purpose, and this says why.
    linkedCustomerArchived:
      "The customer this link points to is archived, so a new document cannot be addressed to them. Choose another customer.",
    // §9.3's duplicate diff, read on ARRIVAL at the new document. Counted
    // nouns take the colon form (D-030) — correct for 1, 2 and 5 alike.
    diffTitle: "Copied from {{no}}",
    diffNone: "Every line kept its price, and nothing was left out.",
    diffRepriced: "Lines repriced: {{count}}.",
    diffDropped: "Lines left out: {{count}}.",
    diffDroppedLine: "Line {{line}} — {{product}}",
    diffDroppedWhy:
      "These products have no price on this customer's list, so they were left out. Add them back with a price you type.",
    // §5's revision history — collapsed until asked for.
    versionsTitle: "Versions",
    versionsShow: "Show versions",
    versionsHide: "Hide versions",
    versionsBody: "What the earlier versions of this quote said.",
    versionsError: "Could not load the versions",
    versionLines: "Lines: {{count}}",
    // The quote's Notifications card (P3-E PR-E1, D-291) — on every quote,
    // draft included: a draft made from a customer's request has already
    // queued the acknowledgement.
    notificationsTitle: "What the customer was told",
    notificationsEmpty: "No message has gone to the customer about this quote yet.",
    notificationsError: "Could not load the messages to the customer",
    // §3.4's explicit cancel (owner, facility admin, group admin).
    cancelQuote: "Cancel quote",
    cancelReason: "Why is it being cancelled?",
    cancelReasonHint: "Kept on the quote and in the audit trail.",
    cancelConfirm: "Cancel it",
    cancelKeep: "Keep it",
    cancelling: "Cancelling…",
    cancelWarning: "The customer's link stops working as soon as you do this.",
    cancelFailed: "Could not cancel the quote.",
    // §3.4's "sales records rejection with reason".
    recordRejection: "Record rejection",
    rejectReason: "Reason",
    rejectReasonRequired: "Pick a reason",
    rejectReasonPrice: "Price",
    rejectReasonLeadTime: "Lead time",
    rejectReasonStock: "Stock",
    rejectReasonOther: "Something else",
    rejectNote: "Note",
    rejectNoteHint: "What the customer said.",
    rejectConfirm: "Record it",
    rejectKeep: "Never mind",
    rejectRecording: "Recording…",
    rejectFailed: "Could not record the rejection.",
    // §2.12's one reason column, read back on the document (audit row 31). Two
    // doors write it and they mean different things, so they are labelled
    // differently: one is what the CUSTOMER said, the other is why WE stopped.
    rejectedBecause: "Why the customer said no:",
    cancelledBecause: "Why it was cancelled:",
    convertedTo: "Became order",
    // E2-1 (spec 09 §4.4, D-312 ruling 4): the deal a quote belongs to. The
    // chip names the deal the reader's deal door answers; a deal it refuses
    // reads as no deal (`opportunity: null` on the wire). A composer opened
    // from a deal (`opportunity_id`) links at create. Refusals are
    // `sales.pipeline.refusal.*`.
    deal: "Deal",
    dealNone: "No deal",
    // The quote is on a deal this reader's deal door refuses (#293, the
    // detail's `opportunity_linked` with `opportunity` null). No Link is
    // offered, because the link door would answer QUOTE_LINKED_ELSEWHERE.
    dealHidden: "On a deal you cannot open",
    dealChip: "{{no}} · {{title}}",
    dealLink: "Link to a deal",
    dealLinkTitle: "Link this quote to a deal",
    dealLinkHint: "The customer's open deals.",
    dealLinkEmpty: "This customer has no open deal.",
    dealLinkNoneWritable: "This customer has no open deal you can link this quote to.",
    dealLinkNoneYet:
      "None of the deals read so far can be linked here. Show more reads further deals.",
    dealLinkConfirm: "Link",
    dealLinking: "Linking…",
    dealUnlink: "Unlink",
    dealUnlinking: "Unlinking…",
    dealLinkFailed: "Could not change the quote's deal.",
    dealFromPage: "This quote will belong to the deal it was started from.",
  },

  // ---- §4.7 orders ---------------------------------------------------------
  orders: {
    title: "Orders",
    tableCaption: "Orders",
    colNo: "Order",
    colCustomer: "Customer",
    colStatus: "Status",
    colTotal: "Total",
    colOwner: "Owner",
    colCredit: "Credit",
    colConfirmed: "Confirmed",
    colPromise: "Promise date",
    // The customer's own PO number (§2.16), on the list so a duplicate-PO
    // warning can be checked against the other order, and so finance can
    // reconcile the export against the number the customer quotes.
    colPo: "Customer PO",
    filterStatus: "Status",
    filterAll: "All",
    // §4.7 declares four filters; status was the only one built until
    // 2026-09-10, and the wire has carried customer_id and facility_id since
    // P3-C-1 (audit row 29). The at-risk toggle landed with P3-J J-3, once
    // module 03's promise watch fills the flag it filters.
    filterCustomer: "Customer",
    filterCustomerHint: "Customer name or number",
    filterFacility: "Facility",
    filterAtRisk: "At risk only",
    atRiskReason: {
      promise_broken: "Promise broken",
      promise_at_risk: "Promise at risk",
      date_passed: "Date passed",
    },
    atRiskEmptyTitle: "No order is at risk",
    atRiskEmptyBody: "Every open order is on track for its date.",
    // §4.19's link from an import job (D-359).
    filterImport: "Loaded from",
    filterImportClear: "Show all orders",
    importRefusedTitle: "These orders are not shown to you",
    importRefusedBody:
      "Only the person who ran this import, or an admin, can list the orders it loaded.",
    // A filtered empty page is not the same fact as an empty order book, and
    // saying so is what stops a reader concluding the factory sold nothing.
    emptyFilteredTitle: "No orders match these filters",
    emptyFilteredBody: "Widen the status, customer or facility.",
    sortAsc: "Sort by “{{column}}”, ascending",
    sortDesc: "Sort by “{{column}}”, descending",
    emptyTitle: "No orders yet",
    // Two doors into the order book since P3-D PR-D1 (D-283): the §3.5
    // acceptance and the §4.4 composer's order mode.
    emptyBody:
      "No orders yet — orders appear when quotes are accepted or when a manager places one.",
    // §4.7's "New order" → the §4.4 composer in order mode (`/sales/orders/new`),
    // offered to `sales.order.write` holders (P3-D PR-D1, D-283).
    new: "New order",
    error: "Could not load the orders",
    loading: "Loading the orders…",
    // §4.7's promise-date column, stated absent rather than rendered as a
    // column of blanks (D-033) — module 03 owns the date and is not built.
    status: {
      pending_approval: "Awaiting approval",
      confirmed: "Confirmed",
      in_production: "In production",
      ready: "Ready",
      shipped: "Shipped",
      delivered: "Delivered",
      closed: "Closed",
      cancelled: "Cancelled",
    },
    credit: {
      passed: "Credit OK",
      blocked_credit: "Over the credit limit",
      blocked_prepay: "Prepayment required",
      override_approved: "Hold released",
    },
    detail: {
      crumbRoot: "Orders",
      error: "Could not load the order",
      timeline: "Where this order is",
      promised: "Promised",
      promiseStatus: {
        committed: "Committed",
        at_risk: "At risk",
        broken: "Broken",
        kept: "Kept",
      },
      promiseChecking: "Promise date being checked",
      confirmedNoticePending: "Sends with the delivery date",
      checkDateLine: "Check date for line {{line}}",
      rePromiseLine: "Re-promise line {{line}}",
      promiseProjected: "now {{date}}",
      promiseRisk: {
        stock_consumed: "Stock used elsewhere",
        material_delay: "Material late",
        capacity_loss: "Capacity lost",
        schedule_slip: "Schedule slipped",
      },
      lineLabel: "{{order}} · line {{line}}",
      linesTitle: "Lines",
      colProduct: "Product",
      colQty: "Quantity",
      colShipped: "Shipped",
      colPrice: "Unit price",
      colTotal: "Line total",
      colMode: "Fulfilment",
      colReservations: "Reserved stock",
      colLineStatus: "Status",
      mode: {
        stock: "From stock",
        offcut_combo: "Off-cut combination",
        production: "To be produced",
        mixed: "Mixed",
      },
      lineStatus: {
        open: "Open",
        in_production: "In production",
        ready: "Ready",
        shipped: "Shipped",
        cancelled: "Cancelled",
      },
      reservation: "Unit {{n}}",
      noReservations: "Nothing reserved — this line is produced.",
      // A CANCELLED line keeps its reservation ids as history (D-285 ruling
      // 6) — the holds read `released` in module 06, so they render as plain
      // text under this sentence rather than as live links into a unit card
      // that no longer holds anything for this order.
      reservationsReleased: "Released when the line was cancelled.",
      notificationsTitle: "What the customer was told",
      notifEmpty: "No messages have gone out for this order yet.",
      degradedTitle: "Fulfilment changed after acceptance",
      degradedLine: "Line {{line}}: planned {{planned}}, now {{actual}}",
      degradedImpact: "Discount difference: {{amount}}",
      trackingTitle: "The customer's tracking link",
      trackingCopy: "Copy the link",
      trackingCopied: "Copied",
      trackingHint:
        "The customer follows this order on its own page. Copy the link and send it to them.",
      trackingOpen: "Open the page",
      cancel: "Cancel the order",
      cancelReason: "Why is it cancelled?",
      cancelConfirm: "Cancel it",
      cancelKeep: "Keep it",
      cancelling: "Cancelling…",
      cancelFailed: "Could not cancel the order.",
      // ---- the LINE cancel (§5 POST /v1/orders/{id}/lines/{lineId}/cancel,
      // P3-D PR-D2, D-285). A per-row control in an actions column; the
      // confirm step renders in the toolbar below the table (the S5
      // form-in-cell lesson, office.css), names the line it is about, and
      // discloses BEFORE the press that the last open line takes the order
      // with it (§9.18) — the shape `sales.overrides.revokeNote` follows.
      colActions: "Actions",
      lineCancel: "Cancel line",
      lineCancelTarget: "Cancelling line {{n}} — {{product}}",
      lineCancelReason: "Why is this line cancelled?",
      lineCancelConfirm: "Cancel the line",
      lineCancelKeep: "Keep it",
      lineCancelling: "Cancelling…",
      lineCancelFailed: "Could not cancel the line.",
      lineCancelLast: "This is the last open line — cancelling it cancels the whole order.",
      // The hint's SECOND sentence (P3-D PR-D3, D-286 ruling 9): when every
      // OTHER non-cancelled line is already `shipped`, the press closes the
      // order as SHIPPED through the ship path's own transition body — never
      // as cancelled (D-285 ruling 11 discharged). Disclosed before the press.
      lineCancelLastShipped:
        "This is the last open line — cancelling it closes the order as shipped.",
      poRef: "Customer PO",
      // §4.7: an order loaded by the open orders lane (D-353 ruling 5).
      imported: "Imported from an open orders file",
      quoteLink: "From quote {{no}}",
      // ---- the direct order's `pending_approval` state (P3-D PR-D1, D-283).
      // The header names WHICH DESK the order waits for (`review.required_
      // approver`, served only while pending — D-255's shape); the approver
      // family is a computed key over the contract's `RequiredApprover` enum
      // and is registered in tools/ci/i18n-parity.ts's DYNAMIC_FAMILIES.
      reviewPending: "Awaiting a decision from {{approver}}",
      approver: {
        facility_admin: "a facility admin",
        group_admin: "a group admin",
      },
      openReviews: "Open the review inbox",
      // §3.6: the placing manager cannot withdraw a pending order (cancel is
      // own + confirmed only); an admin's reject or explicit cancel is the
      // remedy, said beside the pending note rather than discovered as a 403
      // (D-088 — no control whose press refuses).
      cancelAdminOnly: "Only an admin can withdraw an order that is awaiting a decision.",
      // Why the Cancel control is ABSENT on an order that is otherwise
      // cancellable (D-088 + D-286 ruling 11): a shipment holds picks, or goods
      // have left. Shown inside the shipments panel, beside the documents it is
      // about.
      cancelBlockedByShipments:
        "This order can no longer be cancelled here. Cancel an open shipment first; goods that have shipped come back through a return.",
      // Per-line facts stored on §2.17 since migration 0061: the chips every
      // reader gets (booleans are never §7-redacted), and the two money
      // columns that exist only when the payload carried the keys.
      lineNeedsApproval: "Needs approval",
      lineOffList: "Off list",
      colListPrice: "List price",
      colMargin: "Margin",
      // ---- §4.7's shipments panel, progress cell, ship-queue link and Mark
      // delivered (P3-D PR-D3, D-286). The panel is a register table of the
      // order's `shipments[]`; the progress cell reads `shipped of ordered`
      // over the D-041 decimal text; Mark delivered is the office's door on
      // the carrier's word (`sales.order.deliver`), confirmed in the ONE
      // toolbar block the cancel family already uses.
      shipmentsTitle: "Shipments",
      colShipmentNo: "Shipment",
      colShipmentStatus: "Status",
      colShipmentLines: "Lines",
      colCarrier: "Carrier",
      colShippedAt: "Shipped",
      colDeliveredAt: "Delivered",
      openShipQueue: "Open in the ship queue",
      markDelivered: "Mark delivered",
      deliverTitle: "Confirm delivery on the carrier's word",
      deliverConfirm: "Yes, delivered",
      deliverKeep: "Not yet",
      deliverFailed: "Could not mark the order delivered.",
      progressOf: "{{shipped}} of {{ordered}}",
      // The §4.7 pending that remains. The shipments panel and the
      // shipped-quantity bars LANDED with PR-D3 (D-286), the proforma with
      // PR-D4 (D-288) and the work-order chips with the peg (D-290); each
      // sentence left with its feature (D-033).
      promisePending: "The promise date is set by planning.",
      // ---- §4.7's work-order chips (D-290): one per work order made for a
      // line — its number, its status (production's own words,
      // `production.status.wo.*`) and how much of it is made, in the work
      // order's unit. "Plan a work order" opens production's create form
      // already tied to the line.
      colPromise: "Promise date",
      colProduction: "Production",
      woMade: "{{good}} of {{planned}} {{uom}} made",
      noWorkOrders: "No work order yet.",
      planWorkOrder: "Plan a work order",
      // Why Cancel is ABSENT while a line is in production (D-290): the
      // service refuses with ORDER_HAS_ACTIVE_WOS, and the remedy is in
      // production, not here.
      cancelBlockedByWos:
        "This order cannot be cancelled while a line is in production. Cancel its work orders in production first.",
      // ---- the proforma, the status override and the delivery-note column
      // (P3-D PR-D4, D-288). The proforma is a plain link to the door, offered
      // from `confirmed` on, never on a cancelled order, and only to a reader
      // holding `sales.read_value` (rulings 2, 19).
      proforma: "Proforma invoice (PDF)",
      // The create's typed warning, shown once on the order it is about. The
      // duplicate-PO sentence is NEUTRAL: it names the PO and opens the
      // customer's orders, and never tells a manager to cancel an order they
      // may not be able to cancel.
      duplicatePo:
        "Another open order for this customer already carries the customer PO {{po}}. Check that it is not the same order placed twice.",
      duplicatePoLink: "This customer's orders",
      warningUnknown: "The order was placed, with a warning this screen cannot name yet.",
      // Requote: a new draft quote from this order's lines, repriced at
      // today's lists. Desktop only, like the quote's Duplicate.
      requote: "Quote again",
      requoting: "Making the quote…",
      requoteFailed: "Could not make a quote from this order.",
      requoteNoLines:
        "None of this order's products has a price on today's lists, so no quote was made.",
      colDeliveryNote: "Delivery note",
      // §3.6's audited manual transition, confirmed in the ONE toolbar block
      // the cancel and Mark delivered already use. The body sentence depends on
      // the chosen target, because a forced close messages nobody while a
      // forced ready or shipped tells the customer (ruling 12).
      override: "Force status",
      overrideTitle: "Move this order forward by hand",
      overrideTarget: "New status",
      overrideBody: {
        ready:
          "This marks the order ready and tells the customer. It does not ship anything, change any line or release any stock.",
        // Ruling 21: a forced shipped RELEASES the holds on what did not
        // ship — every door that could release them later is closed to a
        // shipped order — so the sentence says so before the press.
        shipped:
          "Marks the order shipped and tells the customer. Holds on the unshipped rest are released; shipped quantities stay as they are.",
        closed:
          "This closes the order. The customer is not messaged, and no line, shipment or stock changes.",
      },
      // D-286 ruling 25, as a fact on the read (`facility_writable`): a
      // reader whose write scope does not cover the order's facility is
      // offered none of the fenced controls, and this says why instead of
      // letting them vanish.
      // Planning names the "Plan a work order" link (D-290): the pegged
      // release refuses the same reader 403, so the link is withheld too.
      facilityFenced:
        "This order ships from a facility you can't access. Someone there handles shipping, status and its work orders.",
      overrideReason: "Why is the status changed by hand?",
      overrideConfirm: "Change the status",
      overrideKeep: "Keep it",
      overriding: "Saving…",
      overrideFailed: "Could not change the order's status.",
      // The refusals the override door can answer (ruling 8), each with its
      // remedy. The select offers only the arcs legal from the current status,
      // so these arrive when the order moved under an open dialog.
      overrideError: {
        STATUS_REGRESSION: "An order only moves forward. Pick a later step.",
        STATUS_OVERRIDE_NOT_ALLOWED:
          "This step cannot be forced here. Production, delivery and cancellation each have their own action.",
        ORDER_NOTHING_SHIPPED:
          "Nothing on this order has shipped yet. An order with nothing shipped is cancelled, not marked shipped.",
        SHIPMENT_OPEN:
          "A shipment of this order is still open. Ship it or cancel it on the floor first.",
        ORDER_TERMINAL: "This order is closed or cancelled, so its status can no longer change.",
        // Ruling 21: a forced shipped over a line module 04 is still making.
        ORDER_HAS_ACTIVE_WOS:
          "The work orders for this order must be cancelled in production first.",
      },
    },
  },

  // ---- §2.18 shipment status (P3-D PR-D3, D-286) ---------------------------
  // A computed family (`sales.shipments.status.<status>`) over the contract's
  // `ShipmentStatus` enum, registered in tools/ci/i18n-parity.ts's
  // DYNAMIC_FAMILIES so a sixth member would fail the build rather than reach
  // the order detail as a raw dotted key. Read by the §4.7 shipments panel's
  // chip; the floor's ship screens read the same words.
  shipments: {
    status: {
      picking: "Picking",
      packed: "Packed",
      shipped: "Shipped",
      delivered: "Delivered",
      cancelled: "Cancelled",
    },
    // ---- the delivery note (P3-D PR-D4, D-288 rulings 10, 11, 14). Drawn
    // ONCE at pack and frozen. A shipment with no note on file is NOT always
    // a failed draw: shipments packed before notes existed, and the seeded
    // ones, have none either — so the words are neutral. A
    // `sales.shipment.post` holder can draw it; anyone else reads that there
    // is none.
    deliveryNote: "Delivery note (PDF)",
    deliveryNoteRender: "Draw the delivery note",
    deliveryNoteRendering: "Drawing…",
    deliveryNoteMissing: "No delivery note on file.",
    deliveryNoteFailed: "Could not draw the delivery note.",
    // 422 DOCUMENT_GLYPH_COVERAGE on the re-render, office and floor alike.
    // Follows `sales.documents.glyphsNamed`, which lists the characters; this
    // says where they live.
    deliveryNoteGlyphs:
      "They come from the order (the customer, the delivery address or the product names) or from the document header or footer in the sales settings.",
    // D-337 ruling 4: a reader without `sales.read_value` is served a copy of
    // the note drawn on demand with no prices, which prints «Copy without
    // prices» itself. The link says so before it is opened.
    deliveryNoteUnpriced: "Delivery note without prices (PDF)",
  },

  // ---- the sales documents' shared refusal (P3-D PR-D4, D-288) -------------
  // 422 DOCUMENT_GLYPH_COVERAGE names every character no bundled font can
  // print (`code_points`). lib/documents.ts `glyphCharactersSentence` prints
  // each as the character AND its U+ code — `😀 (U+1F600)` — so a manager can
  // find it in the text that holds it; the caller appends where that text is.
  documents: {
    glyphsNamed: "The document fonts cannot print these characters: {{characters}}.",
    glyphsUnnamed: "The document has characters the document fonts cannot print.",
    // The first ten, then a count: a description in a script the fonts lack
    // can hold hundreds.
    glyphsMore: "{{list}} and {{rest}} more",
  },

  // ---- §4.8 the FLOOR's ship queue + pick screen (P3-D PR-D3, D-286) ------
  // The floor's block lives in this module's dictionary exactly as
  // `production.floor.*` sits in production.i18n.ts and `inventory.floor.*`
  // in inventory.i18n.ts: the words belong to the module whose document they
  // are about. The screens under areas/floor-pwa import these keys and never
  // edit them (one owner per file). Every refusal the pick door can answer
  // has a sentence here, resolved through lib/floor-errors.ts by code.
  floor: {
    ship: {
      tile: "Ship",
      tileSub: "Pick, pack and ship orders",
      queueTitle: "Ship queue",
      queueEmpty: "Nothing to ship at this facility.",
      badge: "{{reserved}} of {{total}} lines reserved",
      title: "Pick {{orderNo}}",
      open: "Start picking",
      scan: "Scan a bobbin",
      lineOpen: "{{open}} {{uom}} still to pick",
      unitLine: "{{serial}} · {{location}} · {{remaining}} {{uom}}",
      qty: "Length to pick",
      remains: "picks {{qty}} {{uom}} → {{remaining}} {{uom}} remains",
      confirm: "Confirm pick",
      fullUnit: "Take the whole unit",
      pack: "Pack",
      ship: "Ship",
      carrier: "Carrier",
      tracking: "Tracking reference",
      last: "This shipment completes the order.",
      reservedForOther: "Reserved for {{ref}} — not this order.",
      notReserved: "This unit is not reserved for this order.",
      override: "Take it anyway (admin)",
      notShippableLocation: "Put it away first — it is still in {{kind}}.",
      exceedsUnit: "Only {{remaining}} {{uom}} left on this unit.",
      exceedsLine: "Only {{remaining}} {{uom}} still open on this line.",
      alreadyPicked:
        "Already picked from this unit on this shipment — undo it to change the length.",
      unpick: "Undo",
      pending: "{{count}} picks pending sync",
      // ---- the delivery note on the floor (P3-D PR-D4, D-288). A link
      // whenever a note is on file. Opening it is a NETWORK act on a screen
      // that works offline, so without signal the screen says so instead of
      // opening a tab that fails; a pack still waiting on this device has no
      // note yet, and a packed shipment with no note on file offers the draw.
      // "No note on file", never "could not be drawn": a shipment packed
      // before notes existed has none either, and nothing failed.
      deliveryNote: "Delivery note",
      deliveryNoteOffline: "The delivery note needs signal.",
      deliveryNoteQueued:
        "Packed on this device. The delivery note is drawn once the pack goes through.",
      deliveryNoteNone: "No delivery note on file.",
      deliveryNoteDraw: "Draw the delivery note",
      deliveryNoteDrawing: "Drawing…",
      // ---- the screens' own states (the §4 preamble: every list and every
      // document carries loading / empty / error / needs-signal). The block
      // above is the plan's §3.4 list; these are the sentences the two floor
      // screens read beyond it, landed here because this dictionary has one
      // owner and the floor only reads it.
      //
      // A read that did not come back is NOT an empty queue: "nothing to
      // ship" is a claim this device cannot make off a failed request, and a
      // storekeeper who believed it would leave a truck waiting (the counts
      // list's rule, inventory.floor.counts.loadFailedBody).
      queueEmptyBody: "Orders appear here once they are confirmed for this facility.",
      queueLoadFailedTitle: "Could not load the ship queue",
      queueLoadFailedBody: "This is not the same as having nothing to ship. Try again.",
      // The noun `inventory.floor.cache.needConnectionBody` completes: "Find
      // signal once to load {{what}}."
      thisQueue: "the ship queue",
      thisOrder: "this order",
      loadFailedTitle: "Could not load this order's shipment",
      loadFailedBody: "Try again, or ask the office whether the order is still open.",
      // Opening a shipment is the one press on the pick screen that needs the
      // server (the number and the pick plan are minted there). Queued into a
      // dead zone it is SAVED, and the screen says what happens next rather
      // than looking as if the tap did nothing.
      openFailedTitle: "Could not start picking",
      openNeedsSignal:
        "Saved on this device. The shipment opens as soon as there is signal, and picking starts then.",
      // A refused pick, undo, pack or ship: the title over the code's own
      // sentence (the block above) or the server's.
      failedTitle: "That did not go through",
      // Shown on a shipment that has shipped, been delivered or been
      // cancelled: nothing on the screen is a control any more.
      doneBody: "This shipment is closed. There is nothing left to pick on it.",
      // The scan. A unit outside the pick plan is resolved by the SERVER
      // (§9.9), which needs signal; a payload the server does not know is said
      // back to the operator so a mis-typed serial is visible as typed.
      scanNeedsSignal: "This unit is not in the pick plan, and looking it up needs signal.",
      scanNoMatch: "No stock unit matches {{payload}}.",
      // An off-plan unit on an order with more than one open line: the screen
      // cannot guess which line it serves.
      chooseLine: "Choose the line this unit is for.",
      // Lines exist, none takes a pick, and the picks do not close the order
      // (a line in production, or every open line already picked here).
      noOpenLines: "Nothing more can be picked on this order right now.",
      // The keypad's own pre-check, before any request: the wire takes a
      // positive decimal with at most three places (D-041).
      qtyInvalid: "Enter a quantity above zero, with no more than three decimals.",
      pickedRow: "{{qty}} {{uom}} from {{serial}}",
      packedBody: "Packed. Add the carrier if you know it, then ship.",
      // 409 `UNIT_NOT_SHIPPABLE`, `reason: no_lot` — a refusal the plan's list
      // did not carry and the wire does (module 06's seam, before any
      // movement): 0008 admits a unit with no lot, §2.19's shipment line does
      // not, because the recall trace joins the shipped unit to its lot. The
      // remedy is a receipt against a lot, which is what the sentence names.
      notShippableNoLot: "This unit has no lot, so it cannot ship. Receive it against a lot first.",
      // 409 `UNIT_NOT_SHIPPABLE`, `reason: wrong_product` (D-286 ruling 30).
      // The seam used to answer `unit_status` here with `unit_status: "filled"`,
      // which sent the storekeeper to inspect a drum that was perfectly
      // healthy. The unit is simply the wrong product for this line, and the
      // remedy is the next scan.
      notShippableWrongProduct:
        "This unit holds a different product from the order line. Scan a unit of the ordered product.",
    },
  },

  // ---- §4.6 review inbox ---------------------------------------------------
  // The list exports (customers, quotes, orders). Only line 2 of a file is
  // translated — the provenance row; headers are wire key names and cells are
  // wire values. Counted nouns use the colon form, as elsewhere in this file.
  export: {
    button: "Export CSV",
    walking: "Exporting… rows so far: {{count}}",
    cappedNote:
      "Rows in the file: {{count}}. More rows match these filters than one file holds — narrow the filters to export the rest.",
    failed: "Could not export the list — no file was saved.",
    list: {
      orders: "Orders",
      quotes: "Quotes",
      customers: "Customers",
      // E2-1 (spec 09 §4.14): the deal list's file follows §4.1's rules.
      opportunities: "Deals",
    },
    // A sales reader's scope, per list: the customers read is assigned
    // customers only; the orders and quotes reads also return the rows the
    // reader owns, and the deal read returns the deals it owns and those on
    // its assigned customers (§7 narrowing 15).
    scopeAssigned: {
      orders: "assigned customers' orders and this account's own orders",
      quotes: "assigned customers' quotes and this account's own quotes",
      customers: "assigned customers only",
      opportunities: "assigned customers' deals and this account's own deals",
    },
    scopeReadable: "rows this account can read",
    filters: "filters: {{filters}}",
    noFilters: "no filters",
    sortAsc: "sorted by {{column}}, ascending",
    sortDesc: "sorted by {{column}}, descending",
    exportedAt: "exported {{at}}",
    complete: "all rows matching: {{count}}",
    capped: "first rows matching: {{count}}; more exist",
    moneyOmitted: "money columns this account cannot see are left out",
    // D-294 ruling 7: the file says how it is punctuated, so a file forwarded
    // to someone on another locale carries the instructions for opening it.
    punctuationCommaDot: "comma-separated, decimal point",
    punctuationSemicolonComma: "semicolon-separated, decimal comma",
  },
  // ---- TASKS (P3-E PR-E2, D-292 rulings 1-4). ONE vocabulary for the
  // `/sales/tasks` list and the open-task card on the order and the quote. A
  // task the system creates carries NO prose on the wire — its `origin`,
  // `reason_code` and subject are the message (D-225) — so the words below are
  // the only place a system task is ever put into a sentence.
  tasks: {
    title: "Tasks",
    views: { myDay: "My day", overdue: "Overdue" },
    scopeLabel: "Whose tasks",
    scopeMine: "Mine",
    scopeAll: "Everyone's",
    statusLabel: "Show",
    statusOpen: "Open",
    statusDone: "Done",
    // A task the system created closes itself when what it chased is settled
    // (the order was collected, the quote answered): `cancelled` on the wire.
    // The segment holds the system's own closes and a person's withdrawals
    // (D-313 ruling 2); each row's chip says which.
    statusCancelled: "Closed",
    new: "New task",
    loading: "Loading tasks…",
    error: "Could not load the tasks",
    tableCaption: "Tasks",
    emptyOpenTitle: "Nothing to do",
    emptyOpenBody:
      "Tasks you add appear here, and so do the ones created when a customer cannot be reached or has not answered.",
    emptyDoneTitle: "No completed tasks",
    emptyDoneBody: "Tasks you complete are listed here, most recent first.",
    emptyCancelledTitle: "No closed tasks",
    emptyCancelledBody: "Tasks that closed themselves and tasks someone withdrew are listed here.",
    emptyFilteredTitle: "No tasks for this customer",
    emptyFilteredBody: "Clear the customer filter to see every task.",
    filterCustomer: "Customer",
    filterCustomerHint: "Type a customer name",
    filterAll: "Every customer",
    colSubject: "About",
    colWhat: "What",
    colOrigin: "Source",
    colDue: "Due",
    colAssignee: "Assigned to",
    colStatus: "Status",
    colActions: "Actions",
    kind: {
      call: "Call",
      meeting: "Meeting",
      todo: "To do",
    },
    // WHY a system task exists (`reason_code`), in the manager's words.
    reason: {
      READY_NOT_COLLECTED: "Ready order not collected",
      QUOTE_NO_ANSWER: "Quote got no answer",
      CUSTOMER_OPTED_OUT: "Customer opted out of messages",
      NO_CHANNEL: "Customer has no messaging channel",
      other: "Follow up with the customer",
    },
    // WHERE a task came from (`origin`), as the chip beside it.
    origin: {
      manual: "Added by hand",
      reminder_cap_out: "Reminders ran out",
      push_no_channel: "Message not sent",
    },
    state: {
      open: "Open",
      done: "Done",
      cancelled: "Closed automatically",
    },
    // Read out beside a red due date — the colour is never the only signal.
    overdue: "Overdue",
    doneAt: "Done {{when}}",
    cancelledAt: "Closed {{when}}",
    // `cancel_reason` — the one close a task can record without a person.
    // §2.23 closes a system task when its order or quote moved on, or (D-317)
    // when its customer was archived, and the row does not say which.
    cancelReason: {
      PREMISE_ENDED:
        "No longer needed: the order or the quote moved on, or the customer was archived.",
      // E2-1 (D-313 ruling 2): a person withdrew a manual task.
      WITHDRAWN: "Withdrawn. Nobody needs to act on it any more.",
    },
    subjectNone: "No customer or document",
    complete: "Complete",
    completing: "Completing…",
    completeFailed: "Could not complete the task.",
    // 409 ACTIVITY_NOT_OPEN — the row on the glass was stale.
    notOpen:
      "This task is already closed — someone completed it, or it closed itself. The list is up to date now.",
    // 404 ACTIVITY_NOT_FOUND.
    notFound: "This task is no longer visible to you. The list is up to date now.",
    // 403 — visible, but neither theirs nor an admin's.
    notYours: "Only the person the task is assigned to, or an admin, can complete it.",
    // ---- the create form ----
    createTitle: "New task",
    kindLabel: "Kind",
    titleLabel: "What to do",
    titleRequired: "Say what the task is.",
    titleTooLong: "Keep it to 200 characters.",
    noteLabel: "Note",
    noteTooLong: "Keep the note to 2000 characters.",
    dueLabel: "Due",
    dueRequired: "Pick the day it is due.",
    subjectLabel: "About",
    customerLabel: "Customer",
    customerHint: "Type to find a customer (optional)",
    customerNone: "No customer",
    quoteLabel: "Quote",
    orderLabel: "Order",
    documentNone: "None",
    assigneeLabel: "Assign to",
    assigneeMe: "Me",
    // 422 ACTIVITY_ASSIGNEE_NOT_ELIGIBLE: the assignee does not hold the task
    // code, or cannot open the customer, quote or order the task is about.
    assigneeNotEligible:
      "This person cannot take this task: pick an active sales manager or admin who can open the customer, quote or order it is about.",
    subjectNotFound: "That customer, quote or order is not one you can see.",
    // 400 VALIDATION_FAILED `subject_mismatch` — the quote or the order named
    // does not belong to the customer named (or to each other).
    subjectMismatch: "The quote and the order must belong to the customer chosen.",
    createFailed: "Could not create the task.",
    create: "Create task",
    creating: "Creating…",
    // ---- the open-task card on the order and the quote ----
    cardTitle: "Open tasks",
    cardEmptyOrder: "No open tasks on this order.",
    cardEmptyQuote: "No open tasks on this quote.",
    cardError: "Could not load the open tasks",
    cardMore: "More open tasks are listed on the Tasks screen.",
    add: "Add a task",
    openList: "Open the task list",
    // ---- E2-1 (spec 09 §3.12, §4.12, D-313) ----
    // The filters now live in the address (`scope`, `status`, `due`,
    // `customer`, `assignee`); the assignee control is a literal admin's.
    filterDue: "Due",
    dueAny: "Any day",
    // `due`'s four windows (D-313 ruling 3), keyed by the wire token.
    due: {
      overdue: "Overdue",
      today: "Today",
      through_today: "Today or earlier",
      upcoming: "Next 7 days",
    },
    filterAssignee: "Assigned to",
    assigneeAny: "Anyone",
    clearFilters: "Clear filters",
    // Filtered-empty for any filter set, beside PR-E2's customer-only pair.
    emptyFiltersTitle: "No tasks match these filters",
    emptyFiltersBody: "Widen the due date, the customer or the assignee.",
    // The Kind column, hidden by default.
    colKind: "Kind",
    // The deal as a task's subject, and in the create form.
    dealLabel: "Deal",
    dealNone: "No deal",
    cardEmptyDeal: "No open tasks on this deal.",
    cardEmptyCustomer: "No open tasks for this customer.",
    // 400 `subject_mismatch` at `opportunity_id`: the deal is another
    // customer's, or the quote named belongs to another deal.
    subjectMismatchDeal:
      "The deal must belong to the customer chosen, and a quote on another deal cannot be named with it.",
    // 404 OPPORTUNITY_NOT_FOUND in the subject chain.
    subjectNotFoundDeal: "That deal is not one you can see.",
    // Move to another day: the assignee or an admin, on an open row.
    move: "Move to another day",
    moveTitle: "Move to another day",
    moveDueLabel: "New due date",
    moveConfirm: "Move",
    moving: "Moving…",
    moveFailed: "Could not move the task.",
    // Edit: a manual task's words (kind, title, note), and its due date.
    edit: "Edit",
    editTitle: "Edit the task",
    editConfirm: "Save changes",
    editing: "Saving…",
    editFailed: "Could not save the task.",
    // 400 `system_task_words`: the system wrote this task's words.
    systemWords: "The system wrote this task's words, so they cannot be changed.",
    // 403 on PATCH: visible, but neither the assignee's nor an admin's.
    changeNotYours: "Only the person the task is assigned to, or an admin, can change it.",
    // Reassign: a literal admin's.
    reassign: "Reassign",
    reassignTitle: "Reassign the task",
    reassignLabel: "Assign to",
    reassignConfirm: "Reassign",
    reassigning: "Reassigning…",
    reassignFailed: "Could not reassign the task.",
    // Withdraw: a manual task, by an admin or by the assignee who wrote it.
    withdraw: "Withdraw",
    withdrawTitle: "Withdraw this task?",
    withdrawBody: "It moves to the closed tasks as withdrawn. Nothing is deleted.",
    withdrawConfirm: "Withdraw",
    withdrawing: "Withdrawing…",
    withdrawFailed: "Could not withdraw the task.",
    // 403 on withdraw.
    withdrawNotYours:
      "Only the person who wrote the task for themselves, or an admin, can withdraw it.",
    // 409 ACTIVITY_NOT_WITHDRAWABLE: a system task.
    notWithdrawable:
      "The system created this task, so it cannot be withdrawn. It closes itself once its reason ends.",
    // The closed list shows a withdrawn task beside the automatic close.
    withdrawn: "Withdrawn",
  },

  // ---- SALES SETTINGS (P3-E PR-E2, D-292 ruling 11). The §2.22 row as a
  // screen: the reminder sequences, quiet hours, automatic delivery
  // confirmation, the two price toggles, and when the automatic checks last
  // ran — the one line that proves they run at all.
  settings: {
    title: "Sales settings",
    loading: "Loading the settings…",
    error: "Could not load the sales settings",
    readOnlyNote: "You can read these settings. Group and facility admins change them.",
    engineTitle: "Automatic checks",
    engineLastRan: "Last ran {{ago}} ({{when}})",
    engineNotYet: "Not run yet.",
    engineBody:
      "About once a minute the system sends due reminders, expires old quotes and confirms unconfirmed deliveries.",
    activeSince:
      "Reminders go only for orders and quotes that became ready or were sent from {{when}} on.",
    agoNow: "less than a minute ago",
    agoMinutes: "{{n}} min ago",
    agoHours: "{{n}} h ago",
    agoDays: "{{n}} d ago",
    readyTitle: "Ready order not collected",
    readyBody:
      "Remind the customer that a ready order is waiting. After the last reminder, their manager gets a task.",
    followUpTitle: "Quote follow-up",
    followUpBody:
      "Nudge a customer who hasn't answered a quote. It stops when they reply, then the manager gets a task.",
    afterDays: "Days between reminders",
    maxSends: "Reminders at most",
    offHint: "0 turns the messages off; the manager still gets the task.",
    tiersTitle: "By price tier",
    tiersBody: "Leave a cell empty to use the setting above.",
    colTier: "Price tier",
    colReadyAfter: "Ready: days between",
    colReadyMax: "Ready: at most",
    colFollowAfter: "Follow-up: days between",
    colFollowMax: "Follow-up: at most",
    tiersCaption: "Reminder settings by price tier",
    inherit: "Default: {{value}}",
    quietTitle: "Quiet hours",
    quietBody: "No messages go out between these times, in the plant's time zone.",
    quietStart: "From",
    quietEnd: "Until",
    autoConfirmTitle: "Delivery confirmed automatically",
    autoConfirmBody: "An unconfirmed shipped order counts as delivered after this many days.",
    autoConfirmDays: "Days after shipping",
    pricesTitle: "Prices customers see",
    trackingPrices: "Show prices on the order tracking page",
    deliveryNotePrices: "Show prices on the delivery note",
    save: "Save settings",
    saving: "Saving…",
    saved: "Saved.",
    saveFailed: "Could not save the settings.",
    fixFields: "Some values are out of range — see the marked fields.",
    range: "Enter a whole number from {{min}} to {{max}}.",
    time: "Enter a time as hours and minutes, for example 21:00.",
    // ---- E2-1 (spec 09 §4.13, D-312 ruling 2): the Pipeline stages card.
    // Stage names come from the pack's `name_i18n`, never from here.
    // `sales.tier.manage` edits the two numbers; everyone else reads them.
    stagesTitle: "Pipeline stages",
    stagesBody:
      "The stages a deal moves through. Probability weights the forecast; a deal past its days is marked stale.",
    stagesCaption: "Pipeline stages",
    stagesColStage: "Stage",
    stagesColProbability: "Probability, %",
    stagesColRotting: "Stale after, days",
    stagesRottingNever: "Never",
    stagesRottingHint: "Leave it empty and a deal never goes stale in this stage.",
    stagesProbabilityRange: "Enter a whole number from 0 to 100.",
    stagesRottingRange: "Enter a whole number of days from 1 to 365, or leave it empty.",
    stagesSave: "Save",
    stagesSaving: "Saving…",
    stagesSaved: "Saved.",
    stagesSaveFailed: "Could not save the stage.",
    stagesLoading: "Loading the pipeline stages…",
    stagesError: "Could not load the pipeline stages",
    // 404 PIPELINE_STAGE_NOT_FOUND on the PATCH.
    stagesGone: "This stage no longer exists. The list is up to date now.",
    // A 403 for a reader the role mirror cannot judge (spec 09 §4.13's
    // sentence, word for word).
    stagesRefused: "Pipeline stages are not shown to your role",
  },

  reviews: {
    title: "Reviews",
    intro: "What needs a decision, oldest first.",
    kind: {
      quote_margin_breach: "Margin breach · quote",
      order_margin_breach: "Margin breach · order",
      price_override: "Price override",
      credit_block: "Credit block",
      fulfillment_degradation: "Fulfilment changed",
    },
    filterKind: "Kind",
    amount: "Amount",
    // A price override's figure is a RATE per base unit, not a document
    // total — one label for both would put two money frames under one word
    // on the one screen whose job is "what is wrong at a glance".
    amountUnit: "Unit price",
    impact: "Impact",
    requester: "Asked by",
    age: "Waiting, days: {{count}}",
    ageToday: "Came in today",
    open: "Open the document",
    // A price override has no document of its own — it is a row on the
    // customer, listed on the §4.2 Pricing tab, which is where "open" lands.
    openCustomerPricing: "Open the customer's prices",
    line: "Line {{line}}",
    lineBreach: "{{margin}}% against a {{guard}}% guard",
    lineImpact: "Difference: {{amount}}",
    approve: "Approve",
    reject: "Reject",
    deciding: "Deciding…",
    note: "Note",
    noteRequired: "A reason is required to reject.",
    // Shown when the server says this reader cannot decide THIS card — §4.6's
    // "threshold copy stated", so a facility admin reads why instead of
    // pressing into a 403.
    thresholdGroup:
      "This one needs a group admin: the breach is over 2 points, or a line is large.",
    // The override tier is BREACH DEPTH ALONE (D-263): an override is a rate,
    // not a line, so §3.4's money bar does not apply and the copy must not
    // claim it does.
    thresholdOverride:
      "This one needs a group admin: the negotiated price is more than 2 points under the guard.",
    cannotDecide: "Deciding this card is somebody else's grant.",
    informational: "Nothing to decide here — the order already exists. This card is the notice.",
    // A decide answered 409 ORDER_NOT_PENDING / ORDER_TERMINAL: the order is
    // no longer waiting (decided elsewhere, or withdrawn — §3.6). The card
    // leaves the feed as a decided one does, and this line says why it is gone.
    // (`pendingOrderDecide` — "approved from the order slice" — left with
    // P3-D PR-D1, D-283: the order kind's decide route is on the wire and the
    // card carries its Approve / Reject like the other three.)
    departed:
      "{{no}} is no longer waiting for a decision — another desk decided it, or an admin withdrew it.",
    creditApprove: "Release the hold",
    creditPrepay: "Record a prepayment",
    creditUphold: "Keep the hold",
    creditOver: "Over the limit by {{amount}}",
    creditPrepayOnly: "This customer pays in advance.",
    decideFailed: "Could not record the decision.",
    emptyTitle: "Nothing is waiting",
    emptyBody:
      "Margin breaches, negotiated prices, credit blocks and fulfilment changes land here.",
    error: "Could not load the review inbox",
  },

  // §4.9 SALES ANALYTICS (P3-E PR-E3, D-293). The words a sales manager reads
  // about their own numbers, so the register is plain and the nouns are the
  // ones already settled in this file: «ценовая категория» for tier, never a
  // new word for the same thing.
  //
  // TWO ABSENCES ARE NAMED, NOT HIDDEN (D-293 ruling 3). Average response time
  // needs module 17's message timestamps and the off-cut tile needs module
  // 06's stock value; neither is this table's, so each says what is missing
  // rather than showing a zero that would read as a measurement.
  analytics: {
    title: "Sales analytics",
    intro: "How the desk is doing, by manager and price tier.",
    loading: "Loading the numbers…",
    error: "Could not load the analytics",
    empty: "Nothing was quoted or ordered in this range.",
    emptyBody: "Try a wider date range.",
    managerYou: "You",
    rangeFrom: "From",
    rangeTo: "To",
    rangeShown: "{{from}} to {{to}}",
    tierFilter: "Price tier",
    allTiers: "Every tier",
    lastMonth: "Last 30 days",
    lastQuarter: "Last 90 days",
    // TODAY IS SHOWN, and it is not final (§3.10, D-293 amendment (g)): days
    // up to the rollup's watermark are settled, later ones are counted live
    // off the documents and can still move. The screen says which is which
    // rather than presenting one number with two meanings.
    freshness: "Settled up to {{date}}. Later days are counted live and can still change.",
    freshnessNone: "Nothing has settled yet. Every day here is counted live and can still change.",
    colManager: "Manager",
    colTier: "Price tier",
    colCurrency: "Currency",
    colQuotesSent: "Quotes sent",
    colWinRate: "Win rate",
    colOffListRate: "Off-list lines",
    colBreaches: "Margin breaches",
    colOrders: "Orders",
    colOrderValue: "Order value",
    colOffcutValue: "Off-cut revenue",
    tableCaption: "Sales performance by manager, price tier and currency",
    // A rate with no denominator is not 0% (see winRate/offListRate) — the
    // screen prints this instead, so nobody reads "sent nothing" as "won
    // nothing".
    noRate: "—",
    noRateHint: "No quotes were sent in this range, so there is no rate to show.",
    valuesHidden: "Order values are hidden for your role.",
    trendTitle: "Off-list rate over time",
    trendBody: "The share of priced lines sold off the list, by day.",
    trendEmpty: "No priced lines in this range.",
    // §10.13's drill-through: the lines behind the rate, so a sales lead can
    // name them instead of arguing about a percentage.
    drillTitle: "Off-list lines — {{tier}}",
    drillCaption: "The lines behind this manager's off-list rate",
    drillDocument: "Document",
    drillLine: "Line",
    drillDated: "Counted on",
    drillWhat: "What was sold",
    drillQty: "Quantity",
    drillResolved: "Resolved price",
    drillTyped: "Price charged",
    drillDelta: "Difference",
    drillBreach: "Margin",
    drillBreached: "Under guard",
    drillEmpty: "No off-list lines in this range.",
    drillError: "Could not load the off-list lines",
    drillTruncated: "More lines than fit on one page. Narrow the range to see the rest.",
    // E2-1 (spec 09 §4.14): the link card to the forecast, shown to a
    // reader who can open it.
    forecastTitle: "Pipeline forecast",
    forecastBody: "Open deals by close month, weighted by their stage, beside what was won.",
    forecastLink: "Open the forecast",
  },

  // ---- NEXT STEP (P3-E2 PR E2-1, spec 09 §3.12, D-313 rulings 6 and 7) ----
  // One vocabulary for the chip a deal's card and row, the customer book's
  // column, the 360's header and each peek show: the earliest open task on the
  // subject by its kind (`sales.tasks.kind.*`) and its date, marked when it is
  // overdue or due today, or the flag when there is none. It never shows the
  // task's words or its assignee.
  nextStep: {
    label: "Next step",
    none: "No next step",
    // e.g. "Call · 3 Oct"; the date is formatted by the screen.
    on: "{{kind}} · {{date}}",
    today: "Today",
    overdue: "Overdue",
    // Read beside a red date, so the colour is never the only signal.
    overdueOn: "Overdue since {{date}}",
    schedule: "Schedule",
    scheduleAria: "Schedule the next step for {{name}}",
  },

  // ---- SIDE PEEK (P3-E2 PR E2-1, spec 09 §4.15, D-314) --------------------
  // The record beside its list. Each peek reads the record's own detail GET,
  // and its money follows the same field grant as the full page.
  peek: {
    open: "Peek",
    openNamed: "Peek {{name}}",
    close: "Close",
    openFull: "Open the full page",
    kind: {
      customer: "Customer",
      quote: "Quote",
      order: "Order",
      opportunity: "Deal",
    },
    // The panel's accessible name: "Quote Q-2026-00012".
    title: "{{kind}} {{name}}",
    loading: "Loading…",
    // A 403 or a 404 (spec 09 §4.15's sentence).
    notAvailable: "This record is not available to you",
    error: "Could not load this record",
    // Customer.
    tier: "Price tier",
    status: "Status",
    owner: "Owner",
    ownerNone: "No owner",
    primaryContact: "Primary contact",
    primaryContactNone: "No primary contact",
    openTasks: "Open tasks",
    openTasksNone: "No open tasks",
    openDeals: "Open deals",
    openDealsNone: "No open deals",
    latestQuotes: "Latest quotes",
    latestQuotesNone: "No quotes yet",
    latestOrders: "Latest orders",
    latestOrdersNone: "No orders yet",
    // Quote.
    viewed: "Viewed",
    customer: "Customer",
    peekCustomer: "Peek customer",
    total: "Total",
    validUntil: "Valid until",
    revision: "Revision {{n}}",
    lines: "Lines",
    linesMore: "More lines on the full page: {{count}}",
    deal: "Deal",
    dealNone: "No deal",
    dealHidden: "On a deal you cannot open",
    // Order.
    customerPo: "Customer PO",
    customerPoNone: "No PO",
    credit: "Credit",
    // e.g. "Shipped 400 of 1 600 m".
    shippedOf: "Shipped {{shipped}} of {{ordered}} {{uom}}",
    promisePending: "Promise date not set yet",
    promiseDate: "Promise date: {{date}}",
  },

  // ---- PIPELINE (P3-E2 PR E2-1, spec 09 §3.11, §4.14, D-312) --------------
  // The board, the deal list, the deal page, the forecast and their dialogs.
  // Stage names come from the pack's `name_i18n`, never from here, and the
  // four lost reasons reuse `sales.composer.rejectReason*`, because a lost
  // deal and a rejected quote read one vocabulary (D-312 ruling 9).
  pipeline: {
    views: { rotting: "Stale", needsNextStep: "Needs a next step" },
    title: "Pipeline",
    // The tab strip the three pages share.
    tabsAria: "Pipeline pages",
    tabs: { board: "Board", list: "List", forecast: "Forecast" },
    newDeal: "New deal",
    loading: "Loading the pipeline…",
    // The owner filter (admins) and the customer picker (every reader),
    // shared by the board, the list and the forecast.
    filterOwner: "Owner",
    anyOwner: "Every owner",
    filterCustomer: "Customer",
    filterCustomerHint: "Type a customer name",
    anyCustomer: "Every customer",
    clearFilters: "Clear filters",
    // A deal's words, shared by the card, the row, the page and the peek.
    status: { open: "Open", won: "Won", lost: "Lost" },
    probability: "{{pct}}%",
    stageWithProbability: "{{stage}} · {{pct}}%",
    // The idle chip on a rotting deal (whole days since it entered the stage).
    rotting: "Idle {{count}} d",
    inStageDays: "{{count}} d",
    closeMonth: "Closes {{month}}",
    // Read beside a close month shown in red.
    closeMonthPassed: "Close month passed",
    noValue: "No value",
    weightedValue: "Weighted {{amount}}",
    lostBecause: "Lost: {{reason}}",
    owner: "Owner",
    ownerNone: "No owner",
    // A tenant with no stages (D-312 ruling 3): every reader reads the first
    // pair, and an admin also reads what installs them.
    noStagesTitle: "No pipeline stages yet",
    noStagesBody:
      "Pipeline stages come with the industry pack, and this workspace does not have them yet. Ask your group admin to apply the industry pack update.",
    noStagesAdmin:
      "This workspace has no pipeline stages. Apply the industry pack update to add them.",
    noStagesAction: "Review the industry pack update",

    // ---- the board ----
    board: {
      aria: "Open deals by stage",
      columnAria: "{{stage}}: open deals {{count}}",
      count: "Deals: {{count}}",
      rottingCount: "Stale: {{count}}",
      // A value reader's column totals, one line per currency.
      openTotal: "Open {{amount}}",
      weightedTotal: "Weighted {{amount}}",
      unvaluedCount: "Without a value: {{count}}",
      showMore: "Show more",
      showMoreFailed: "Could not load more deals.",
      columnEmpty: "No open deals in this stage.",
      emptyTitle: "No open deals yet",
      emptyBody: "Start a deal for a customer and move it across the stages as it progresses.",
      filteredEmptyTitle: "No open deals match these filters",
      filteredEmptyBody: "Clear the owner or the customer to see every open deal.",
      error: "Could not load the board",
      // The zones that appear while a card moves.
      wonZone: "Won",
      lostZone: "Lost",
      // Every card's Move button, the one way to every stage on a phone.
      move: "Move",
      moveAria: "Move {{title}}",
      keyboardHint:
        "Space picks up a deal, arrows choose a stage, Space or Enter drops it, Escape cancels.",
      // The polite live region.
      livePicked: "Picked up {{title}} from {{stage}}.",
      liveOver: "Over {{stage}}.",
      liveOverWon: "Over Won. Drop to mark the deal won.",
      liveOverLost: "Over Lost. Drop to give the reason.",
      liveDropped: "{{title}} dropped in {{stage}}.",
      liveCancelled: "Move cancelled. {{title}} is back in {{stage}}.",
      liveWon: "{{title}} marked won.",
      liveLost: "{{title}} marked lost.",
      // A card whose move is on its way to the server.
      pending: "Saving the move…",
      // A refused drop's one line (`role="alert"`) starts with the refusal
      // (`sales.pipeline.refusal.*`); this is the network arm, with Try
      // again reusing the same Idempotency-Key.
      network: "The move was not saved. Check the connection and try again.",
      tryAgain: "Try again",
      // The phone's stage chips.
      stagesAria: "Stages",
      stageChip: "{{stage}} ({{count}})",
    },

    // ---- the deal list ----
    list: {
      title: "Deals",
      caption: "Deals",
      loading: "Loading the deals…",
      error: "Could not load the deals",
      search: "Search deals",
      searchHint: "Title, number or customer",
      filterStatus: "Status",
      filterStage: "Stage",
      anyStage: "Every stage",
      filterCloseFrom: "Close from",
      filterCloseTo: "Close to",
      filterRotting: "Stale only",
      filterNeedsNextStep: "Needs a next step",
      filterLostReason: "Lost reason",
      anyLostReason: "Any reason",
      colDeal: "Deal",
      colCustomer: "Customer",
      colStage: "Stage",
      colValue: "Value",
      colWeighted: "Weighted",
      colCloseMonth: "Close month",
      colNextStep: "Next step",
      colInStage: "In stage",
      colOwner: "Owner",
      colStatus: "Status",
      colUpdated: "Updated",
      sortAsc: "Sort by “{{column}}”, ascending",
      sortDesc: "Sort by “{{column}}”, descending",
      emptyTitle: "No deals yet",
      emptyBody: "Start a deal for a customer and it appears here.",
      filteredEmptyTitle: "No deals match these filters",
      filteredEmptyBody:
        "Widen the status, stage, owner, customer, close months, stale, next step, lost reason or search.",
    },

    // ---- the deal page ----
    deal: {
      number: "Deal {{no}}",
      loading: "Loading the deal…",
      error: "Could not load the deal",
      // A 404, which also answers a deal outside the reader's narrowing.
      notAvailableTitle: "This deal is not available to you",
      notAvailableBody: "It may belong to another owner, or your role does not read it.",
      customer: "Customer",
      stage: "Stage",
      inStage: "In this stage for {{count}} d",
      nextStep: "Next step",
      // Actions, each shown where the deal's own `mutable` and status admit it.
      move: "Move",
      markWon: "Mark won",
      markLost: "Mark lost",
      reopen: "Reopen",
      reopening: "Reopening…",
      reopenFailed: "Could not reopen the deal.",
      // The Details card.
      detailsTitle: "Details",
      titleLabel: "Title",
      valueLabel: "Value",
      valueHint: "The total the customer would pay, VAT included",
      currencyLabel: "Currency",
      closeMonthLabel: "Close month",
      ownerLabel: "Owner",
      save: "Save",
      saving: "Saving…",
      saved: "Saved.",
      saveFailed: "Could not save the deal.",
      // 409 OPPORTUNITY_NOT_OPEN on a PATCH of the value or the month.
      closedFields: "A closed deal keeps its value and close month. Reopen it to change them.",
      // The Quotes card.
      quotesTitle: "Quotes",
      quotesEmpty: "No quote is linked to this deal.",
      createQuote: "Create quote",
      linkQuote: "Link a quote",
      linkTitle: "Link a quote",
      linkHint: "The customer's open quotes that belong to no deal.",
      linkEmpty: "This customer has no open quote without a deal.",
      linkConfirm: "Link",
      linking: "Linking…",
      linkFailed: "Could not link the quote.",
      // The picker's list read failed, and a candidate whose check failed.
      linkListError: "Could not load this customer's quotes.",
      linkUnchecked: "Could not check whether it is on another deal",
      // Every page read so far held nothing to link, and older pages remain.
      linkNoneYet: "None of the quotes read so far can be linked. Show more reads older quotes.",
      unlink: "Unlink",
      unlinking: "Unlinking…",
      unlinkFailed: "Could not unlink the quote.",
      // A linked quote the customer accepted while the deal is still open.
      // A writer reads the instruction beside Mark won; a reader who cannot
      // write reads the fact alone (the page offers that reader no button).
      acceptedNotice: "The customer accepted {{quote}}. Mark the deal won with it.",
      acceptedNoticeReader: "The customer accepted {{quote}}, and the deal is still open.",
      markWonWith: "Mark won with {{quote}}",
      // A won deal whose order was cancelled (`won_order_cancelled`).
      wonCancelled:
        "This deal was won, and its order {{order}} was cancelled afterwards. Reopen it to keep working on it.",
      wonCancelledNoNumber:
        "This deal was won, and its order was cancelled afterwards. Reopen it to keep working on it.",
      // How a won deal was won (`won_via`, `won_quote_no`, `won_order_no`).
      wonWithQuoteAndOrder: "Won with quote {{quote}} and order {{order}}",
      wonWithQuote: "Won with quote {{quote}}",
      wonWithOrder: "Won with order {{order}}",
      wonByAcceptance: "Won when the customer accepted a quote",
      wonByHand: "Marked won by hand",
      closedAt: "Closed {{when}}",
      lostReason: "Why it was lost",
      lostNote: "Note",
      tasksTitle: "Tasks",
      // The source conversation, one link away (`source_conversation_id`).
      sourceTitle: "Source",
      sourceConversation: "Started from a conversation",
      openConversation: "Open the conversation",
    },

    // ---- the forecast ----
    forecast: {
      title: "Forecast",
      loading: "Loading the forecast…",
      error: "Could not load the forecast",
      rangeFrom: "From",
      rangeTo: "To",
      rangeInvalid: "Pick an end month after the start and no more than 24 months later.",
      // One table per currency.
      caption: "Forecast in {{currency}}",
      colMonth: "Month",
      colDeals: "Deals",
      colPipeline: "Pipeline",
      colWeighted: "Weighted",
      colWon: "Won in month",
      colUnvalued: "Without a value",
      // Open deals whose close month has passed stand on their own row first.
      slipped: "Slipped",
      slippedHint: "Open deals whose close month has passed",
      // Each cell's link to the list filtered to its month.
      cellLink: "Open the deals closing in {{month}}",
      // Under each table, when non-zero.
      cancelledWins: "Won deals whose order was cancelled: {{count}}. They add no value.",
      // A sales reader's scope sentence.
      scopeSales: "Your own deals and the deals on your assigned customers.",
      // The value door (§7 narrowing 7): the sentence in the forecast's place,
      // for a reader the mirror knows is refused and after a 403.
      noValue: "The forecast is built from deal values, which are not shown to your role.",
      emptyTitle: "Nothing to forecast in these months",
      emptyBody: "Open deals closing in this range, and deals won in it, appear here.",
      // §3.11: a won deal without a value is counted apart, beside the Won
      // cell of its month, when there are any.
      wonUnvalued: "Won without a value: {{count}}",
      // The owner or customer filter left nothing in the range.
      filteredEmptyTitle: "No deal matches these filters in these months",
      filteredEmptyBody: "Clear the owner and customer filters to see the whole forecast.",
    },

    // ---- New deal ----
    create: {
      title: "New deal",
      titleLabel: "Title",
      customerLabel: "Customer",
      customerHint: "Type a customer name",
      valueLabel: "Value",
      valueHint: "The total the customer would pay, VAT included",
      currencyLabel: "Currency",
      closeMonthLabel: "Close month",
      stageLabel: "Stage",
      ownerLabel: "Owner",
      ownerMe: "Me",
      // Read-only, when the address carries `from_conversation`.
      fromConversation: "From the conversation",
      confirm: "Create deal",
      creating: "Creating…",
      failed: "Could not create the deal.",
      titleRequired: "Give the deal a title.",
      titleTooLong: "Keep the title to 200 characters.",
      customerRequired: "Pick the customer.",
      // A value typed while the customer's currency is still loading.
      valueWaiting:
        "The customer's currency has not loaded yet, so the value cannot be read. Try again in a moment.",
      valueInvalid: "Enter an amount, or leave it empty.",
      closeMonthRequired: "Pick the month you expect to close.",
    },

    // ---- Move (every card's button, and the one way on a phone) ----
    move: {
      title: "Move {{title}}",
      stagesLabel: "Stage",
      closeLabel: "Close the deal",
      current: "{{stage}}, now",
      confirm: "Move",
      moving: "Moving…",
      failed: "Could not move the deal.",
    },

    // ---- Lost ----
    lose: {
      title: "Mark {{title}} lost",
      reasonLabel: "Why was it lost?",
      reasonRequired: "Pick a reason.",
      noteLabel: "Note",
      noteHint: "What the customer said.",
      noteTooLong: "Keep the note to 2000 characters.",
      staysOpen: "Open quotes and tasks on this deal stay open.",
      confirm: "Mark lost",
      saving: "Saving…",
      failed: "Could not mark the deal lost.",
    },

    // ---- Won ----
    win: {
      title: "Mark {{title}} won",
      body: "A won deal leaves the board and counts in the forecast in the month it closed.",
      confirm: "Mark won",
      saving: "Saving…",
      failed: "Could not mark the deal won.",
      // The picker (D-312 ruling 9): the customer's quotes and orders the win
      // door accepts, and no document, which counts the deal's own value.
      pickLabel: "What won the deal",
      pickHint: "Pick the quote or order the customer bought with; the forecast counts its total.",
      none: "No document. The forecast counts the deal's own value.",
      optionQuote: "Quote {{quote}}, order {{order}}",
      optionOrder: "Order {{order}}",
      empty: "This customer has no quote or order that can win this deal.",
      noneYet: "None of the orders read so far can win this deal. Show more reads older orders.",
      listError: "Could not load this customer's orders.",
      // A 403 on the orders read: a refusal, so no request id and no retry.
      ordersRefused:
        "Your role does not read this customer's orders, so the deal is won on its own value.",
    },

    // ---- REFUSALS, keyed by the problem code (D-225: no server prose). One
    // set for the board's alert line, the dialogs, the deal page, the peek and
    // the composer's deal control. Each is followed on the glass by the
    // request id.
    refusal: {
      // `extensions.current_stage_id` names the stage the deal stands in.
      OPPORTUNITY_STAGE_CHANGED: "Someone moved this deal to {{stage}} first. It stays there.",
      OPPORTUNITY_NOT_OPEN: "This deal is already closed.",
      OPPORTUNITY_NOT_CLOSED: "This deal is already open.",
      OPPORTUNITY_NOT_FOUND: "This deal is no longer visible to you.",
      PIPELINE_STAGE_NOT_FOUND: "That stage no longer exists.",
      PIPELINE_NOT_CONFIGURED:
        "No pipeline stages are set up, so a deal can't be created. Apply the industry pack update to add them.",
      OPPORTUNITY_OWNER_NOT_ELIGIBLE:
        "This person cannot own the deal. Pick an active sales manager or admin who can open the customer.",
      OPPORTUNITY_CUSTOMER_MISMATCH:
        "That quote, order or conversation belongs to another customer.",
      OPPORTUNITY_WON_BY_ORDER:
        "The customer's order for this deal still stands. The deal can be reopened once that order is cancelled.",
      CUSTOMER_ARCHIVED: "The customer is archived, so it holds no open deal.",
      CUSTOMER_NOT_FOUND: "That customer is not one you can see.",
      QUOTE_NOT_FOUND: "That quote is not one you can see.",
      ORDER_NOT_FOUND: "That order is not one you can see.",
      QUOTE_TERMINAL: "That quote is finished, so its deal cannot change.",
      QUOTE_LINKED_ELSEWHERE: "That quote already belongs to another deal. Unlink it there first.",
      QUOTE_NOT_CONVERTED: "That quote has not become an order yet.",
      ORDER_NOT_CONFIRMED: "That order is still waiting for approval.",
      ORDER_TERMINAL: "That order was cancelled.",
      ORDER_WON_ELSEWHERE: "That order already won another deal.",
      CONVERSATION_NOT_FOUND: "The conversation this deal comes from is not available.",
      SOURCE_CONVERSATION_UNVERIFIABLE:
        "The conversation could not be checked just now. Try again in a moment.",
      // A 403 on a write door.
      forbidden: "Your role cannot change this deal.",
      // A 403 naming `sales.read_value` on a create or an edit that sent a value.
      valueForbidden: "Your role cannot set a deal's value.",
    },
  },

  // P3-E2 PR E2-2d (D-326, design-system §3.13): the four cells edited in
  // place, on the customer book (Owner, Tags), the deal list (Stage) and the
  // task lists (Done). The cell editor holds no string, so every word it says
  // is one of these: the edit button's name, the list's name, the reason a
  // cell is read-only, and the refusal its danger mark and toast carry.
  cells: {
    owner: {
      edit: "Change the owner of {{name}}",
      list: "Owner of {{name}}",
      filter: "Find a member",
      noMatch: "No member matches.",
      adminOnly: "Only a group admin or a facility admin names a customer's owner.",
      // OWNER_CHANGED: `current_owner_user_id` names who owns it now.
      changed: "Someone else made {{owner}} the owner first. The cell shows the owner now.",
      changedToNone: "Someone else removed the owner first. The customer has no owner now.",
      changedUnnamed: "Someone else changed the owner first. The cell shows the owner now.",
      refused: "The owner was not changed: {{reason}}.",
      failed: "The owner was not changed, because the request got no answer. Try again.",
    },
    tags: {
      edit: "Change the tags of {{name}}",
      list: "Tags of {{name}}",
      apply: "Apply",
      // A mixed commit names each refused tag with its code (D-326 ruling 1).
      refused: "These tags did not change: {{list}}.",
      refusedItem: "{{tag}} ({{reason}}, {{code}})",
      refusedCode: "{{tag}} ({{code}})",
      noAnswer: "{{tag}} (no answer)",
      signedOut: "{{tag}} (signed out)",
      // An archived tag the cell shows is offered so it can be cleared; the
      // attach door refuses it, so it leaves the checklist once it is cleared.
      archivedOption: "{{name}} (archived)",
      failed: "The tags were not changed, because the request got no answer. Try again.",
    },
    stage: {
      edit: "Change the stage of {{title}}",
      list: "Stage of {{title}}",
      closed: "A won or lost deal keeps its stage. Reopen it on its page to move it.",
      notAllowed: "Your role cannot move this deal.",
      failed: "The deal was not moved, because the request got no answer. Try again.",
    },
    // A 401 on any cell: the session ended, so nothing changed and the
    // screen sends the reader to sign in (design-system §3.13).
    signedOut: "Your session has ended, so nothing was changed. Sign in again.",
    done: {
      header: "Done",
      done: "Done",
      notDone: "Not done",
      edit: "Mark done: {{what}}",
      // Done is one way, as `/complete` is (D-326 ruling 5).
      isDone: "This task is done, and a done task stays done.",
      closed: "This task was closed without being done.",
      notAllowed: "Only the assignee or an admin completes this task.",
      failed: "The task was not completed, because the request got no answer. Try again.",
    },
  },
} as const;

// The dictionary SHAPE, with values widened to `string` (src/i18n-shape.ts).
export type SalesDict = Translated<typeof salesEn>;

// ru is TRANSLATED (see sales.ru.ts). fr, zh, zh-Hant and hi are
// machine-translated in their own files (sales.<locale>.ts), which
// src/locales/<locale>.ts imports so each stays in its locale's chunk. The
// claim is declared in src/i18n-completeness.ts and enforced by
// tools/ci/i18n-parity.ts, which fails if a translated value regresses to en.
export const salesRu: SalesDict = salesRuDict;
